import { useState, useEffect, useCallback } from "react";
import {
  Calculator,
  Download,
  Info,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  TrendingUp,
  Users,
  DollarSign,
  Building2,
  Loader2,
  Star,
  ExternalLink,
  Settings,
  Clock,
  HelpCircle,
  Award,
  MapPin,
  Snowflake,
} from "lucide-react";
import { Card, Input, Button, Badge } from "@bcc/shared-ui";
import { formatCurrency, formatNumber } from "@bcc/shared-ui";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import type { AddressAutocompleteResult } from "@bcc/shared-api";
import { useLocation } from "../contexts/LocationContext";
import { SEO } from "../components/SEO";
import { QUANTITY_SURVEY_FAQS } from "../seo/structured-data";

// ============================================================================
// API CONSTANTS
// ============================================================================

const PRESETS_BASE = "/api/v1/presets";
const REPORTS_BASE = "/api/v1/reports";

// ============================================================================
// TYPES
// ============================================================================

interface ProjectTypeInfo {
  code: string;
  name: string;
  description: string;
}

interface PresetSummary {
  key: string;
  name: string;
  description: string;
  area_sf: number;
  quality: string;
  project_type?: string;
}

interface ProjectPresetDetail {
  name: string;
  description: string;
  project_type: string;
  quality_level: string;
  gross_floor_area_sf: number;
  num_floors: number;
  basement_area_sf: number;
  garage_area_sf: number;
  has_basement: boolean;
  has_garage: boolean;
  num_bedrooms: number;
  num_bathrooms: number;
  special_features: string[];
  design_notes: string;
  typical_permit_fees: number;
  applicable_standards: string[];
}

interface QualityLevelInfo {
  code: string;
  name: string;
  cost_range_residential: { low: number; mid: number; high: number };
}

interface DivisionBreakdown {
  division_code: string;
  division_name: string;
  percentage: number;
  amount: number;
}

interface EstimateResult {
  project_type: string;
  quality_level: string;
  gross_floor_area_sf: number;
  cost_per_sf: number;
  construction_subtotal: number;
  division_breakdown: DivisionBreakdown[];
  location_factor: number;
  inflation_factor: number;
  adjusted_construction: number;
  contingency_percent: number;
  contingency_amount: number;
  design_fees_percent: number;
  design_fees_amount: number;
  permit_fees: number;
  other_soft_costs: number;
  total_soft_costs: number;
  total_project_cost: number;
  final_cost_per_sf: number;
}

interface BOQLineItem {
  item_number: string;
  csi_division: string;
  csi_code: string;
  description: string;
  quantity: number;
  unit: string;
  unit_rate: number;
  // Breakdown (calculated from unit_rate based on labour ratio)
  material_rate?: number;
  labour_rate?: number;
  total_amount: number;
  notes: string;
}

// Default labour percentages by CSI Division (Calgary market 2026)
// Sources: CIQS Alberta Chapter, industry benchmarks, local contractor surveys
const DEFAULT_LABOUR_PERCENTAGES: Record<string, number> = {
  "03": 45, // Concrete - moderate labour
  "04": 55, // Masonry - high labour
  "05": 40, // Metals - moderate
  "06": 50, // Wood/Plastics - high labour
  "07": 45, // Thermal/Moisture - moderate
  "08": 35, // Openings (doors/windows) - lower labour
  "09": 60, // Finishes - very high labour (painting, drywall)
  "10": 30, // Specialties
  "11": 25, // Equipment
  "12": 35, // Furnishings
  "21": 55, // Fire Suppression
  "22": 50, // Plumbing - high labour
  "23": 45, // HVAC - moderate
  "26": 55, // Electrical - high labour
  "27": 50, // Communications
  "28": 45, // Electronic Safety
  "31": 35, // Earthwork
  "32": 40, // Exterior Improvements
  "33": 45, // Utilities
};

interface RateSettings {
  labourRateMultiplier: number; // 1.0 = default, 1.1 = 10% higher
  materialRateMultiplier: number; // 1.0 = default
  overheadPercent: number; // Typically 10-15%
  profitPercent: number; // Typically 8-12%
  showBreakdown: boolean;
}

interface BOQSection {
  division_code: string;
  division_name: string;
  line_items: BOQLineItem[];
  section_total: number;
}

interface BOQResult {
  project_name: string;
  sections: BOQSection[];
  subtotal: number;
  contingency_percent: number;
  contingency_amount: number;
  grand_total: number;
}

// Material Price Types
interface PriceSource {
  name: string;
  url: string | null;
  reliability: string;
  update_frequency: string;
}

interface MaterialItem {
  description: string;
  unit: string;
  price: number;
  price_range: { low: number; high: number };
  source: string;
  last_updated: string;
  notes?: string;
}

interface MaterialCategory {
  category: string;
  items: Record<string, MaterialItem>;
}

interface MaterialPricesData {
  metadata: {
    title: string;
    version: string;
    last_updated: string;
    currency: string;
  };
  price_sources: Record<string, PriceSource>;
  materials: Record<string, MaterialCategory>;
  labor_rates: Record<string, { rate: number; source: string; notes: string }>;
  market_trends: Record<
    string,
    {
      trend: string;
      change_ytd_percent: number;
      forecast_2026: string;
      source: string;
    }
  >;
}

interface PriceSyncStatus {
  [source: string]: {
    source_name: string;
    source_url: string | null;
    cache_valid: boolean;
    last_sync: string | null;
    cache_ttl_hours: number;
    description?: string;
    provides?: string[];
    currency?: "CAD" | "USD";
  };
}

interface ScheduledJob {
  id: string;
  name: string;
  next_run: string | null;
  trigger: string;
}

// ============================================================================
// CURRENCY CONVERSION (USD to CAD)
// Bank of Canada rate - updated periodically
// ============================================================================

const DEFAULT_USD_TO_CAD_RATE = 1.44; // As of Jan 2026 - Bank of Canada
const DEFAULT_EXCHANGE_RATE_DATE = "2026-01-24";

// ============================================================================
// PROJECT TYPE DEFAULTS BY ZONE/COMMUNITY (Calgary 2026)
// Typical values based on Calgary market data, zoning requirements, and
// community characteristics. Sources: Calgary Real Estate Board, CMHC, CIQS
// ============================================================================

interface ProjectTypeDefaults {
  gross_floor_area_sf: number;
  num_floors: number;
  basement_area_sf: number;
  garage_area_sf: number;
  quality_level: string;
  contingency_percent: number;
  design_fees_percent: number;
}

// Base defaults for each project type
const PROJECT_TYPE_DEFAULTS: Record<string, ProjectTypeDefaults> = {
  // Residential - Single Family
  residential_single: {
    gross_floor_area_sf: 2200,
    num_floors: 2,
    basement_area_sf: 1000,
    garage_area_sf: 440,
    quality_level: "standard",
    contingency_percent: 10,
    design_fees_percent: 8,
  },
  // Residential - Multi Family (Duplex/Townhouse)
  residential_multi: {
    gross_floor_area_sf: 3500,
    num_floors: 2,
    basement_area_sf: 1200,
    garage_area_sf: 600,
    quality_level: "standard",
    contingency_percent: 12,
    design_fees_percent: 10,
  },
  // Residential - Apartment/Condo
  residential_apartment: {
    gross_floor_area_sf: 15000,
    num_floors: 4,
    basement_area_sf: 5000,
    garage_area_sf: 8000,
    quality_level: "standard",
    contingency_percent: 15,
    design_fees_percent: 12,
  },
  // Commercial - Retail
  commercial_retail: {
    gross_floor_area_sf: 5000,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 0,
    quality_level: "standard",
    contingency_percent: 12,
    design_fees_percent: 10,
  },
  // Commercial - Office
  commercial_office: {
    gross_floor_area_sf: 10000,
    num_floors: 3,
    basement_area_sf: 2000,
    garage_area_sf: 3000,
    quality_level: "premium",
    contingency_percent: 12,
    design_fees_percent: 12,
  },
  // Industrial - Light
  industrial_light: {
    gross_floor_area_sf: 8000,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 0,
    quality_level: "economy",
    contingency_percent: 10,
    design_fees_percent: 6,
  },
  // Industrial - Heavy
  industrial_heavy: {
    gross_floor_area_sf: 25000,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 0,
    quality_level: "economy",
    contingency_percent: 15,
    design_fees_percent: 8,
  },
  // Renovation/Addition
  renovation: {
    gross_floor_area_sf: 800,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 0,
    quality_level: "standard",
    contingency_percent: 20,
    design_fees_percent: 10,
  },
  // Basement Development
  basement_development: {
    gross_floor_area_sf: 1000,
    num_floors: 1,
    basement_area_sf: 1000,
    garage_area_sf: 0,
    quality_level: "standard",
    contingency_percent: 15,
    design_fees_percent: 5,
  },
  // Secondary Suite
  secondary_suite: {
    gross_floor_area_sf: 700,
    num_floors: 1,
    basement_area_sf: 700,
    garage_area_sf: 0,
    quality_level: "standard",
    contingency_percent: 15,
    design_fees_percent: 5,
  },
  // Garage/Carport
  garage: {
    gross_floor_area_sf: 500,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 500,
    quality_level: "economy",
    contingency_percent: 10,
    design_fees_percent: 3,
  },
  // Deck/Patio
  deck: {
    gross_floor_area_sf: 300,
    num_floors: 1,
    basement_area_sf: 0,
    garage_area_sf: 0,
    quality_level: "standard",
    contingency_percent: 10,
    design_fees_percent: 2,
  },
};

// Zone-based adjustments (multipliers for floor area based on typical lot sizes)
const ZONE_ADJUSTMENTS: Record<
  string,
  { areaMultiplier: number; qualityBias: string }
> = {
  // Inner city - smaller lots, higher quality
  "R-C1": { areaMultiplier: 0.85, qualityBias: "standard" },
  "R-C1N": { areaMultiplier: 0.8, qualityBias: "premium" },
  "R-C2": { areaMultiplier: 0.9, qualityBias: "standard" },
  "R-CG": { areaMultiplier: 0.75, qualityBias: "standard" },
  "R-G": { areaMultiplier: 1.2, qualityBias: "standard" },
  // Mixed-use - typically higher quality finishes
  "M-C1": { areaMultiplier: 1.0, qualityBias: "premium" },
  "M-C2": { areaMultiplier: 1.1, qualityBias: "premium" },
  "M-CG": { areaMultiplier: 0.95, qualityBias: "standard" },
  "M-H1": { areaMultiplier: 1.5, qualityBias: "premium" },
  "M-H2": { areaMultiplier: 2.0, qualityBias: "luxury" },
  // Industrial - larger footprints
  "I-G": { areaMultiplier: 1.5, qualityBias: "economy" },
  "I-B": { areaMultiplier: 1.3, qualityBias: "economy" },
  "I-H": { areaMultiplier: 2.0, qualityBias: "economy" },
};

// Community-based adjustments (based on typical home values and construction quality)
const COMMUNITY_ADJUSTMENTS: Record<
  string,
  { areaMultiplier: number; qualityBias: string }
> = {
  // Premium communities - larger homes, higher quality
  "ASPEN WOODS": { areaMultiplier: 1.4, qualityBias: "luxury" },
  BEARSPAW: { areaMultiplier: 1.6, qualityBias: "luxury" },
  BRITANNIA: { areaMultiplier: 1.3, qualityBias: "premium" },
  "ELBOW PARK": { areaMultiplier: 1.35, qualityBias: "luxury" },
  "MOUNT ROYAL": { areaMultiplier: 1.5, qualityBias: "luxury" },
  "SPRINGBANK HILL": { areaMultiplier: 1.35, qualityBias: "premium" },
  "PUMP HILL": { areaMultiplier: 1.3, qualityBias: "premium" },
  "EAGLE RIDGE": { areaMultiplier: 1.25, qualityBias: "premium" },
  BRENTWOOD: { areaMultiplier: 1.1, qualityBias: "premium" },
  VARSITY: { areaMultiplier: 1.05, qualityBias: "standard" },
  // Inner city - infills, often premium finishes but smaller lots
  HILLHURST: { areaMultiplier: 0.9, qualityBias: "premium" },
  SUNNYSIDE: { areaMultiplier: 0.85, qualityBias: "premium" },
  BRIDGELAND: { areaMultiplier: 0.9, qualityBias: "premium" },
  INGLEWOOD: { areaMultiplier: 0.9, qualityBias: "premium" },
  ALTADORE: { areaMultiplier: 0.95, qualityBias: "premium" },
  "MARDA LOOP": { areaMultiplier: 0.95, qualityBias: "premium" },
  RAMSAY: { areaMultiplier: 0.85, qualityBias: "standard" },
  MISSION: { areaMultiplier: 0.8, qualityBias: "premium" },
  BELTLINE: { areaMultiplier: 0.7, qualityBias: "premium" },
  KENSINGTON: { areaMultiplier: 0.85, qualityBias: "premium" },
  // Suburban - standard sizes
  "MCKENZIE TOWNE": { areaMultiplier: 1.0, qualityBias: "standard" },
  CRANSTON: { areaMultiplier: 1.05, qualityBias: "standard" },
  "AUBURN BAY": { areaMultiplier: 1.05, qualityBias: "standard" },
  MAHOGANY: { areaMultiplier: 1.1, qualityBias: "standard" },
  SETON: { areaMultiplier: 1.0, qualityBias: "standard" },
  COPPERFIELD: { areaMultiplier: 0.95, qualityBias: "standard" },
  "NEW BRIGHTON": { areaMultiplier: 0.95, qualityBias: "standard" },
  TUSCANY: { areaMultiplier: 1.1, qualityBias: "standard" },
  "ROCKY RIDGE": { areaMultiplier: 1.15, qualityBias: "standard" },
  "ROYAL OAK": { areaMultiplier: 1.1, qualityBias: "standard" },
  "PANORAMA HILLS": { areaMultiplier: 1.05, qualityBias: "standard" },
  EVANSTON: { areaMultiplier: 1.0, qualityBias: "standard" },
  "SAGE HILL": { areaMultiplier: 1.05, qualityBias: "standard" },
  "NOLAN HILL": { areaMultiplier: 1.0, qualityBias: "standard" },
  KINCORA: { areaMultiplier: 1.0, qualityBias: "standard" },
  "SKYVIEW RANCH": { areaMultiplier: 0.9, qualityBias: "economy" },
  REDSTONE: { areaMultiplier: 0.9, qualityBias: "economy" },
  CORNERSTONE: { areaMultiplier: 0.95, qualityBias: "standard" },
  LIVINGSTON: { areaMultiplier: 0.95, qualityBias: "standard" },
  // Established suburbs
  "LAKE BONAVISTA": { areaMultiplier: 1.2, qualityBias: "premium" },
  "WILLOW PARK": { areaMultiplier: 1.15, qualityBias: "standard" },
  "DEER RUN": { areaMultiplier: 1.1, qualityBias: "standard" },
  WOODBINE: { areaMultiplier: 1.05, qualityBias: "standard" },
  "SIGNAL HILL": { areaMultiplier: 1.1, qualityBias: "standard" },
  "COACH HILL": { areaMultiplier: 1.05, qualityBias: "standard" },
  "STRATHCONA PARK": { areaMultiplier: 1.1, qualityBias: "standard" },
  // Affordable areas
  "FOREST LAWN": { areaMultiplier: 0.8, qualityBias: "economy" },
  MARLBOROUGH: { areaMultiplier: 0.85, qualityBias: "economy" },
  "PENBROOKE MEADOWS": { areaMultiplier: 0.8, qualityBias: "economy" },
  DOVER: { areaMultiplier: 0.85, qualityBias: "economy" },
  FALCONRIDGE: { areaMultiplier: 0.85, qualityBias: "economy" },
  CASTLERIDGE: { areaMultiplier: 0.85, qualityBias: "economy" },
  MARTINDALE: { areaMultiplier: 0.9, qualityBias: "economy" },
  TARADALE: { areaMultiplier: 0.9, qualityBias: "economy" },
};

/**
 * Get project defaults adjusted for zone and/or community
 */
function getAdjustedDefaults(
  projectType: string,
  zoneCode?: string,
  community?: string,
): ProjectTypeDefaults {
  // Start with base defaults for project type
  const baseDefaults =
    PROJECT_TYPE_DEFAULTS[projectType] ||
    PROJECT_TYPE_DEFAULTS.residential_single;

  let areaMultiplier = 1.0;
  let qualityBias = baseDefaults.quality_level;

  // Apply community adjustment first (more specific)
  if (community) {
    const communityUpper = community.toUpperCase();
    const communityAdj = COMMUNITY_ADJUSTMENTS[communityUpper];
    if (communityAdj) {
      areaMultiplier = communityAdj.areaMultiplier;
      qualityBias = communityAdj.qualityBias;
    }
  }

  // If no community match, try zone adjustment
  if (areaMultiplier === 1.0 && zoneCode) {
    const normalizedZone = zoneCode.toUpperCase();
    const zoneAdj = ZONE_ADJUSTMENTS[normalizedZone];
    if (zoneAdj) {
      areaMultiplier = zoneAdj.areaMultiplier;
      qualityBias = zoneAdj.qualityBias;
    }
  }

  // Return adjusted defaults
  return {
    gross_floor_area_sf: Math.round(
      baseDefaults.gross_floor_area_sf * areaMultiplier,
    ),
    num_floors: baseDefaults.num_floors,
    basement_area_sf: Math.round(
      baseDefaults.basement_area_sf * areaMultiplier,
    ),
    garage_area_sf: Math.round(
      baseDefaults.garage_area_sf *
        (areaMultiplier > 1.2 ? areaMultiplier * 0.9 : areaMultiplier),
    ),
    quality_level: qualityBias,
    contingency_percent: baseDefaults.contingency_percent,
    design_fees_percent: baseDefaults.design_fees_percent,
  };
}

// ============================================================================
// FALLBACK PRICE DATA (Calgary 2026 rates when API unavailable)
// Sources: CIQS Alberta, NRCAN, industry benchmarks, local supplier quotes
// All prices converted to CAD where applicable
// ============================================================================

const FALLBACK_MATERIAL_PRICES: MaterialPricesData = {
  metadata: {
    title: "Calgary Construction Material Prices",
    version: "2026.01",
    last_updated: "2026-01-24",
    currency: "CAD",
  },
  price_sources: {
    NRCAN: {
      name: "Natural Resources Canada",
      url: "https://www.nrcan.gc.ca/",
      reliability: "High",
      update_frequency: "Weekly",
    },
    STATSCAN: {
      name: "Statistics Canada BCPI",
      url: "https://www.statcan.gc.ca/",
      reliability: "High",
      update_frequency: "Monthly",
    },
    INDUSTRY: {
      name: "Industry Averages (CIQS)",
      url: "https://ciqs.org/",
      reliability: "High",
      update_frequency: "Quarterly",
    },
    LOCAL: {
      name: "Calgary Supplier Quotes",
      url: null,
      reliability: "Medium",
      update_frequency: "As quoted",
    },
  },
  materials: {
    lumber: {
      category: "Lumber",
      items: {
        spf_2x4: {
          description: "SPF 2×4×8' Stud Grade",
          unit: "EA",
          price: 4.89,
          price_range: { low: 4.25, high: 5.5 },
          source: "NRCAN",
          last_updated: "2026-01-20",
          notes: "Price volatile due to US tariffs",
        },
        spf_2x6: {
          description: "SPF 2×6×8' #2 & Better",
          unit: "EA",
          price: 7.45,
          price_range: { low: 6.8, high: 8.25 },
          source: "NRCAN",
          last_updated: "2026-01-20",
        },
        spf_2x10: {
          description: "SPF 2×10×12' #2 & Better",
          unit: "EA",
          price: 18.95,
          price_range: { low: 17.5, high: 21.0 },
          source: "NRCAN",
          last_updated: "2026-01-20",
        },
        plywood_3_4: {
          description: 'Plywood 3/4" 4×8 Sheathing',
          unit: "SHT",
          price: 62.0,
          price_range: { low: 55.0, high: 72.0 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
        osb_7_16: {
          description: 'OSB 7/16" 4×8 Sheathing',
          unit: "SHT",
          price: 28.5,
          price_range: { low: 24.0, high: 32.0 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
        lvl_beam: {
          description: "LVL Beam 1-3/4×9-1/2×20'",
          unit: "EA",
          price: 145.0,
          price_range: { low: 130.0, high: 165.0 },
          source: "LOCAL",
          last_updated: "2026-01-10",
        },
      },
    },
    concrete: {
      category: "Concrete & Masonry",
      items: {
        concrete_25mpa: {
          description: "Ready-mix Concrete 25 MPa",
          unit: "M³",
          price: 245.0,
          price_range: { low: 225.0, high: 275.0 },
          source: "LOCAL",
          last_updated: "2026-01-18",
          notes: "Delivered, pump extra $800-1200",
        },
        concrete_32mpa: {
          description: "Ready-mix Concrete 32 MPa",
          unit: "M³",
          price: 268.0,
          price_range: { low: 250.0, high: 295.0 },
          source: "LOCAL",
          last_updated: "2026-01-18",
        },
        rebar_15m: {
          description: "Rebar 15M (20' length)",
          unit: "EA",
          price: 18.5,
          price_range: { low: 16.0, high: 22.0 },
          source: "LOCAL",
          last_updated: "2026-01-12",
          notes: "Steel prices volatile - US tariff impact",
        },
        cmu_block: {
          description: "CMU Block 8×8×16 Standard",
          unit: "EA",
          price: 3.85,
          price_range: { low: 3.25, high: 4.5 },
          source: "LOCAL",
          last_updated: "2026-01-10",
        },
      },
    },
    insulation: {
      category: "Insulation",
      items: {
        batt_r20: {
          description: "Fiberglass Batt R-20 (2×6 wall)",
          unit: "SF",
          price: 1.25,
          price_range: { low: 1.05, high: 1.45 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
        batt_r40: {
          description: "Fiberglass Batt R-40 (attic)",
          unit: "SF",
          price: 1.85,
          price_range: { low: 1.6, high: 2.15 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
        spray_foam: {
          description: 'Spray Foam Closed Cell 2"',
          unit: "SF",
          price: 3.5,
          price_range: { low: 2.8, high: 4.25 },
          source: "LOCAL",
          last_updated: "2026-01-08",
          notes: "Installed price",
        },
        rigid_eps: {
          description: 'Rigid EPS 2" R-8',
          unit: "SF",
          price: 1.45,
          price_range: { low: 1.2, high: 1.75 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
      },
    },
    roofing: {
      category: "Roofing",
      items: {
        shingles_arch: {
          description: "Architectural Shingles (30-yr)",
          unit: "SQ",
          price: 145.0,
          price_range: { low: 125.0, high: 185.0 },
          source: "LOCAL",
          last_updated: "2026-01-12",
        },
        metal_standing: {
          description: "Standing Seam Metal Roofing",
          unit: "SF",
          price: 18.5,
          price_range: { low: 15.0, high: 24.0 },
          source: "LOCAL",
          last_updated: "2026-01-05",
          notes: "Material only, installation extra",
        },
        underlayment: {
          description: "Synthetic Roof Underlayment",
          unit: "SF",
          price: 0.35,
          price_range: { low: 0.28, high: 0.45 },
          source: "LOCAL",
          last_updated: "2026-01-10",
        },
      },
    },
    drywall: {
      category: "Drywall & Finishes",
      items: {
        drywall_1_2: {
          description: 'Drywall 1/2" 4×8 Regular',
          unit: "SHT",
          price: 18.5,
          price_range: { low: 16.0, high: 22.0 },
          source: "LOCAL",
          last_updated: "2026-01-18",
        },
        drywall_5_8: {
          description: 'Drywall 5/8" 4×8 Type X',
          unit: "SHT",
          price: 24.0,
          price_range: { low: 21.0, high: 28.0 },
          source: "LOCAL",
          last_updated: "2026-01-18",
        },
        joint_compound: {
          description: "Joint Compound 20kg Pail",
          unit: "EA",
          price: 28.0,
          price_range: { low: 24.0, high: 35.0 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
      },
    },
    electrical: {
      category: "Electrical",
      items: {
        wire_14_2: {
          description: "NMD90 14/2 (75m roll)",
          unit: "ROLL",
          price: 85.0,
          price_range: { low: 75.0, high: 98.0 },
          source: "LOCAL",
          last_updated: "2026-01-10",
        },
        wire_12_2: {
          description: "NMD90 12/2 (75m roll)",
          unit: "ROLL",
          price: 125.0,
          price_range: { low: 110.0, high: 145.0 },
          source: "LOCAL",
          last_updated: "2026-01-10",
        },
        panel_200a: {
          description: "200A Main Panel w/ Breakers",
          unit: "EA",
          price: 650.0,
          price_range: { low: 550.0, high: 800.0 },
          source: "LOCAL",
          last_updated: "2026-01-05",
        },
        outlet_std: {
          description: "Duplex Outlet 15A",
          unit: "EA",
          price: 3.5,
          price_range: { low: 2.5, high: 5.0 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
      },
    },
    plumbing: {
      category: "Plumbing",
      items: {
        pex_1_2: {
          description: "PEX Tubing 1/2\" (100' coil)",
          unit: "COIL",
          price: 65.0,
          price_range: { low: 55.0, high: 78.0 },
          source: "LOCAL",
          last_updated: "2026-01-12",
        },
        pex_3_4: {
          description: "PEX Tubing 3/4\" (100' coil)",
          unit: "COIL",
          price: 95.0,
          price_range: { low: 82.0, high: 115.0 },
          source: "LOCAL",
          last_updated: "2026-01-12",
        },
        water_heater: {
          description: "50 Gal Electric Water Heater",
          unit: "EA",
          price: 850.0,
          price_range: { low: 700.0, high: 1100.0 },
          source: "LOCAL",
          last_updated: "2026-01-08",
        },
        toilet: {
          description: "Toilet Standard 2-Piece",
          unit: "EA",
          price: 285.0,
          price_range: { low: 200.0, high: 450.0 },
          source: "LOCAL",
          last_updated: "2026-01-15",
        },
      },
    },
    hvac: {
      category: "HVAC",
      items: {
        furnace_gas: {
          description: "Gas Furnace 80K BTU 96% Eff",
          unit: "EA",
          price: 2850.0,
          price_range: { low: 2400.0, high: 3500.0 },
          source: "LOCAL",
          last_updated: "2026-01-05",
          notes: "Equipment only",
        },
        ac_unit: {
          description: "Central AC 3-Ton 16 SEER",
          unit: "EA",
          price: 3200.0,
          price_range: { low: 2800.0, high: 4000.0 },
          source: "LOCAL",
          last_updated: "2026-01-05",
        },
        ductwork: {
          description: "Galvanized Duct (per SF home)",
          unit: "SF",
          price: 4.5,
          price_range: { low: 3.5, high: 6.0 },
          source: "INDUSTRY",
          last_updated: "2026-01-01",
        },
      },
    },
  },
  labor_rates: {
    carpenter: {
      rate: 65.0,
      source: "Alberta Construction Assoc.",
      notes: "Journeyman rate, benefits included",
    },
    electrician: {
      rate: 75.0,
      source: "IBEW Local 424",
      notes: "Journeyman rate, benefits included",
    },
    plumber: {
      rate: 72.0,
      source: "UA Local 488",
      notes: "Journeyman rate, benefits included",
    },
    hvac_tech: {
      rate: 70.0,
      source: "Sheet Metal Workers Local 8",
      notes: "Journeyman rate, benefits included",
    },
    labourer: {
      rate: 35.0,
      source: "Market average",
      notes: "General construction labourer",
    },
    drywaller: {
      rate: 55.0,
      source: "Market average",
      notes: "Includes taping/finishing",
    },
    roofer: {
      rate: 58.0,
      source: "Market average",
      notes: "Shingle installation",
    },
    concrete_finisher: {
      rate: 60.0,
      source: "Market average",
      notes: "Flatwork and forming",
    },
  },
  market_trends: {
    lumber: {
      trend: "Stable",
      change_ytd_percent: -5.2,
      forecast_2026:
        "Prices expected to remain stable with US tariff uncertainty",
      source: "NRCAN",
    },
    steel: {
      trend: "Increasing",
      change_ytd_percent: 8.5,
      forecast_2026: "25% Canada counter-tariff on US steel driving prices up",
      source: "STATSCAN",
    },
    concrete: {
      trend: "Stable",
      change_ytd_percent: 2.1,
      forecast_2026: "Modest increases expected due to fuel costs",
      source: "LOCAL",
    },
    copper: {
      trend: "Increasing",
      change_ytd_percent: 12.3,
      forecast_2026: "Strong demand from electrification projects",
      source: "FRED",
    },
  },
};

const FALLBACK_SYNC_STATUS: PriceSyncStatus = {
  NRCAN: {
    source_name: "Natural Resources Canada",
    source_url: "https://www.nrcan.gc.ca/",
    cache_valid: true,
    last_sync: new Date().toISOString(),
    cache_ttl_hours: 168,
    description: "Official Canadian lumber & building material prices (CAD)",
    provides: ["Lumber (SPF, Plywood)", "OSB Sheathing", "Wood Products"],
    currency: "CAD",
  },
  STATSCAN: {
    source_name: "Statistics Canada BCPI",
    source_url: "https://www.statcan.gc.ca/",
    cache_valid: true,
    last_sync: new Date().toISOString(),
    cache_ttl_hours: 720,
    description:
      "Building Construction Price Index & regional cost factors (CAD)",
    provides: [
      "Regional Price Indices",
      "Inflation Adjustments",
      "Labour Costs",
    ],
    currency: "CAD",
  },
  FRED: {
    source_name: "Federal Reserve Economic Data",
    source_url: "https://fred.stlouisfed.org/",
    cache_valid: true,
    last_sync: new Date().toISOString(),
    cache_ttl_hours: 168,
    description: "US commodity prices & PPI (USD → converted to CAD)",
    provides: [
      "Steel Prices",
      "Copper/Aluminum",
      "Concrete Materials",
      "PPI Trends",
    ],
    currency: "USD",
  },
  API_NINJAS: {
    source_name: "API Ninjas Commodities",
    source_url: "https://api-ninjas.com/",
    cache_valid: true,
    last_sync: new Date().toISOString(),
    cache_ttl_hours: 168,
    description:
      "Real-time global commodity spot prices (USD → converted to CAD)",
    provides: ["Copper", "Aluminum", "Steel HRC", "Zinc", "Natural Gas"],
    currency: "USD",
  },
};

// ============================================================================
// FALLBACK BOQ GENERATOR (when API unavailable)
// Generates realistic BOQ based on Calgary 2026 construction costs
// ============================================================================

interface BOQFormData {
  project_name: string;
  gross_floor_area_sf: number;
  num_floors: number;
  num_bedrooms: number;
  num_bathrooms: number;
  has_basement: boolean;
  basement_area_sf: number;
  has_garage: boolean;
  garage_area_sf: number;
  quality_level: string;
  contingency_percent: number;
}

// Quality multipliers for Calgary market (Standard = 1.0)
const QUALITY_MULTIPLIERS: Record<string, number> = {
  economy: 0.75,
  standard: 1.0,
  premium: 1.35,
  luxury: 1.85,
};

function generateFallbackBOQ(form: BOQFormData): BOQResult {
  const qualityMult = QUALITY_MULTIPLIERS[form.quality_level] || 1.0;
  const totalArea =
    form.gross_floor_area_sf +
    (form.has_basement ? form.basement_area_sf : 0) +
    (form.has_garage ? form.garage_area_sf * 0.5 : 0); // Garage at 50% cost

  const sections: BOQSection[] = [];

  // Division 03 - Concrete
  const concreteItems: BOQLineItem[] = [
    {
      item_number: "03.01",
      csi_division: "03",
      csi_code: "03 30 00",
      description: "Foundation concrete (4000 PSI)",
      quantity: Math.round(form.gross_floor_area_sf * 0.15),
      unit: "CY",
      unit_rate: 285 * qualityMult,
      total_amount: 0,
      notes: "Includes forming, rebar, pour",
    },
    {
      item_number: "03.02",
      csi_division: "03",
      csi_code: "03 30 00",
      description: "Basement slab on grade",
      quantity: form.has_basement ? Math.round(form.basement_area_sf / 9) : 0,
      unit: "CY",
      unit_rate: 265 * qualityMult,
      total_amount: 0,
      notes: '6" thick with vapor barrier',
    },
    {
      item_number: "03.03",
      csi_division: "03",
      csi_code: "03 11 00",
      description: "Concrete formwork",
      quantity: form.has_basement ? Math.round(form.basement_area_sf * 0.4) : 0,
      unit: "SFCA",
      unit_rate: 12.5 * qualityMult,
      total_amount: 0,
      notes: "Foundation walls",
    },
  ];
  concreteItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  const concreteTotal = concreteItems.reduce((s, i) => s + i.total_amount, 0);
  if (concreteTotal > 0) {
    sections.push({
      division_code: "03",
      division_name: "Concrete",
      line_items: concreteItems.filter((i) => i.quantity > 0),
      section_total: concreteTotal,
    });
  }

  // Division 06 - Wood & Plastics
  const woodItems: BOQLineItem[] = [
    {
      item_number: "06.01",
      csi_division: "06",
      csi_code: "06 11 00",
      description: "Floor framing (joists, rim board)",
      quantity: Math.round(totalArea),
      unit: "SF",
      unit_rate: 8.75 * qualityMult,
      total_amount: 0,
      notes: 'I-joists 16" O.C.',
    },
    {
      item_number: "06.02",
      csi_division: "06",
      csi_code: "06 11 00",
      description: "Wall framing (2×6 exterior)",
      quantity: Math.round(form.gross_floor_area_sf * 1.1),
      unit: "SF",
      unit_rate: 7.25 * qualityMult,
      total_amount: 0,
      notes: "R-24 ready",
    },
    {
      item_number: "06.03",
      csi_division: "06",
      csi_code: "06 11 00",
      description: "Roof trusses",
      quantity: Math.round(form.gross_floor_area_sf / form.num_floors),
      unit: "SF",
      unit_rate: 9.5 * qualityMult,
      total_amount: 0,
      notes: "Engineered wood trusses",
    },
    {
      item_number: "06.04",
      csi_division: "06",
      csi_code: "06 16 00",
      description: 'Subfloor sheathing (3/4" T&G)',
      quantity: Math.round(totalArea),
      unit: "SF",
      unit_rate: 3.85 * qualityMult,
      total_amount: 0,
      notes: "AdvanTech or equivalent",
    },
    {
      item_number: "06.05",
      csi_division: "06",
      csi_code: "06 40 00",
      description: "Interior trim & millwork",
      quantity: Math.round(form.gross_floor_area_sf),
      unit: "SF",
      unit_rate: 6.5 * qualityMult,
      total_amount: 0,
      notes: "Baseboards, casings, crown",
    },
  ];
  woodItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "06",
    division_name: "Wood, Plastics & Composites",
    line_items: woodItems,
    section_total: woodItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 07 - Thermal & Moisture Protection
  const thermalItems: BOQLineItem[] = [
    {
      item_number: "07.01",
      csi_division: "07",
      csi_code: "07 21 00",
      description: "Batt insulation (R-24 walls)",
      quantity: Math.round(form.gross_floor_area_sf * 0.9),
      unit: "SF",
      unit_rate: 2.45 * qualityMult,
      total_amount: 0,
      notes: "Mineral wool",
    },
    {
      item_number: "07.02",
      csi_division: "07",
      csi_code: "07 21 00",
      description: "Attic insulation (R-60)",
      quantity: Math.round(form.gross_floor_area_sf / form.num_floors),
      unit: "SF",
      unit_rate: 3.25 * qualityMult,
      total_amount: 0,
      notes: "Blown cellulose",
    },
    {
      item_number: "07.03",
      csi_division: "07",
      csi_code: "07 31 00",
      description: "Asphalt shingles (30-year)",
      quantity: Math.round((form.gross_floor_area_sf / form.num_floors) * 1.15),
      unit: "SF",
      unit_rate: 5.85 * qualityMult,
      total_amount: 0,
      notes: "Architectural grade",
    },
    {
      item_number: "07.04",
      csi_division: "07",
      csi_code: "07 46 00",
      description: "Vinyl siding",
      quantity: Math.round(form.gross_floor_area_sf * 0.7),
      unit: "SF",
      unit_rate: 7.25 * qualityMult,
      total_amount: 0,
      notes: "Premium grade with trim",
    },
  ];
  thermalItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "07",
    division_name: "Thermal & Moisture Protection",
    line_items: thermalItems,
    section_total: thermalItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 08 - Openings
  const windowCount =
    form.num_bedrooms * 2 + form.num_bathrooms + form.num_floors * 4;
  const doorCount = form.num_bedrooms + form.num_bathrooms + 3;
  const openingsItems: BOQLineItem[] = [
    {
      item_number: "08.01",
      csi_division: "08",
      csi_code: "08 11 00",
      description: "Exterior entry door (fiberglass)",
      quantity: 2,
      unit: "EA",
      unit_rate: 1850 * qualityMult,
      total_amount: 0,
      notes: "Front + rear entry",
    },
    {
      item_number: "08.02",
      csi_division: "08",
      csi_code: "08 14 00",
      description: "Interior doors (hollow core)",
      quantity: doorCount,
      unit: "EA",
      unit_rate: 385 * qualityMult,
      total_amount: 0,
      notes: "Pre-hung with hardware",
    },
    {
      item_number: "08.03",
      csi_division: "08",
      csi_code: "08 51 00",
      description: "Vinyl windows (double-hung)",
      quantity: windowCount,
      unit: "EA",
      unit_rate: 650 * qualityMult,
      total_amount: 0,
      notes: "Low-E, argon filled",
    },
    {
      item_number: "08.04",
      csi_division: "08",
      csi_code: "08 36 00",
      description: "Garage door (insulated)",
      quantity: form.has_garage ? (form.garage_area_sf > 300 ? 2 : 1) : 0,
      unit: "EA",
      unit_rate: 1450 * qualityMult,
      total_amount: 0,
      notes: "16×7 steel insulated",
    },
  ];
  openingsItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "08",
    division_name: "Openings",
    line_items: openingsItems.filter((i) => i.quantity > 0),
    section_total: openingsItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 09 - Finishes
  const finishesItems: BOQLineItem[] = [
    {
      item_number: "09.01",
      csi_division: "09",
      csi_code: "09 29 00",
      description: 'Drywall (1/2" taped & finished)',
      quantity: Math.round(totalArea * 3.2),
      unit: "SF",
      unit_rate: 3.15 * qualityMult,
      total_amount: 0,
      notes: "Level 4 finish",
    },
    {
      item_number: "09.02",
      csi_division: "09",
      csi_code: "09 91 00",
      description: "Interior painting",
      quantity: Math.round(totalArea * 3.5),
      unit: "SF",
      unit_rate: 1.85 * qualityMult,
      total_amount: 0,
      notes: "2 coats, premium latex",
    },
    {
      item_number: "09.03",
      csi_division: "09",
      csi_code: "09 65 00",
      description: "Laminate flooring",
      quantity: Math.round(form.gross_floor_area_sf * 0.6),
      unit: "SF",
      unit_rate: 8.5 * qualityMult,
      total_amount: 0,
      notes: "12mm with underlayment",
    },
    {
      item_number: "09.04",
      csi_division: "09",
      csi_code: "09 30 00",
      description: "Ceramic tile (bathrooms)",
      quantity: form.num_bathrooms * 120,
      unit: "SF",
      unit_rate: 18.5 * qualityMult,
      total_amount: 0,
      notes: "Floor & tub surround",
    },
    {
      item_number: "09.05",
      csi_division: "09",
      csi_code: "09 68 00",
      description: "Carpet (bedrooms)",
      quantity: form.num_bedrooms * 150,
      unit: "SF",
      unit_rate: 6.75 * qualityMult,
      total_amount: 0,
      notes: "40oz nylon with pad",
    },
  ];
  finishesItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "09",
    division_name: "Finishes",
    line_items: finishesItems,
    section_total: finishesItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 22 - Plumbing
  const plumbingItems: BOQLineItem[] = [
    {
      item_number: "22.01",
      csi_division: "22",
      csi_code: "22 40 00",
      description: "Plumbing rough-in",
      quantity: form.num_bathrooms + 1,
      unit: "FIX",
      unit_rate: 2850 * qualityMult,
      total_amount: 0,
      notes: "Per fixture group",
    },
    {
      item_number: "22.02",
      csi_division: "22",
      csi_code: "22 42 00",
      description: "Water heater (tankless)",
      quantity: 1,
      unit: "EA",
      unit_rate: 3200 * qualityMult,
      total_amount: 0,
      notes: "Rinnai or equivalent",
    },
    {
      item_number: "22.03",
      csi_division: "22",
      csi_code: "22 41 00",
      description: "Kitchen fixtures",
      quantity: 1,
      unit: "LS",
      unit_rate: 2400 * qualityMult,
      total_amount: 0,
      notes: "Sink, faucet, disposal",
    },
    {
      item_number: "22.04",
      csi_division: "22",
      csi_code: "22 42 00",
      description: "Bathroom fixtures",
      quantity: form.num_bathrooms,
      unit: "SET",
      unit_rate: 2100 * qualityMult,
      total_amount: 0,
      notes: "Toilet, vanity, tub/shower",
    },
  ];
  plumbingItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "22",
    division_name: "Plumbing",
    line_items: plumbingItems,
    section_total: plumbingItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 23 - HVAC
  const hvacItems: BOQLineItem[] = [
    {
      item_number: "23.01",
      csi_division: "23",
      csi_code: "23 81 00",
      description: "Furnace (high-efficiency)",
      quantity: 1,
      unit: "EA",
      unit_rate: 4500 * qualityMult,
      total_amount: 0,
      notes: "96% AFUE, 80,000 BTU",
    },
    {
      item_number: "23.02",
      csi_division: "23",
      csi_code: "23 31 00",
      description: "Ductwork",
      quantity: Math.round(totalArea),
      unit: "SF",
      unit_rate: 6.25 * qualityMult,
      total_amount: 0,
      notes: "Insulated flex & rigid",
    },
    {
      item_number: "23.03",
      csi_division: "23",
      csi_code: "23 84 00",
      description: "Air conditioning (central)",
      quantity: 1,
      unit: "EA",
      unit_rate: 5200 * qualityMult,
      total_amount: 0,
      notes: "3-ton, 16 SEER",
    },
    {
      item_number: "23.04",
      csi_division: "23",
      csi_code: "23 37 00",
      description: "HRV system",
      quantity: 1,
      unit: "EA",
      unit_rate: 2800 * qualityMult,
      total_amount: 0,
      notes: "Heat recovery ventilator",
    },
  ];
  hvacItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "23",
    division_name: "HVAC",
    line_items: hvacItems,
    section_total: hvacItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 26 - Electrical
  const electricalItems: BOQLineItem[] = [
    {
      item_number: "26.01",
      csi_division: "26",
      csi_code: "26 24 00",
      description: "Electrical panel (200A)",
      quantity: 1,
      unit: "EA",
      unit_rate: 2850 * qualityMult,
      total_amount: 0,
      notes: "Main breaker panel",
    },
    {
      item_number: "26.02",
      csi_division: "26",
      csi_code: "26 05 00",
      description: "Electrical rough-in",
      quantity: Math.round(form.gross_floor_area_sf),
      unit: "SF",
      unit_rate: 8.5 * qualityMult,
      total_amount: 0,
      notes: "Wiring, boxes, circuits",
    },
    {
      item_number: "26.03",
      csi_division: "26",
      csi_code: "26 51 00",
      description: "Light fixtures",
      quantity:
        form.num_bedrooms * 2 + form.num_bathrooms * 2 + form.num_floors * 8,
      unit: "EA",
      unit_rate: 185 * qualityMult,
      total_amount: 0,
      notes: "LED throughout",
    },
    {
      item_number: "26.04",
      csi_division: "26",
      csi_code: "26 27 00",
      description: "Devices (switches/outlets)",
      quantity: Math.round(form.gross_floor_area_sf / 40),
      unit: "EA",
      unit_rate: 45 * qualityMult,
      total_amount: 0,
      notes: "Decora style",
    },
  ];
  electricalItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "26",
    division_name: "Electrical",
    line_items: electricalItems,
    section_total: electricalItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 12 - Furnishings (Cabinets)
  const furnishingsItems: BOQLineItem[] = [
    {
      item_number: "12.01",
      csi_division: "12",
      csi_code: "12 35 00",
      description: "Kitchen cabinets",
      quantity: 1,
      unit: "LS",
      unit_rate: 12500 * qualityMult,
      total_amount: 0,
      notes: "Full kitchen set",
    },
    {
      item_number: "12.02",
      csi_division: "12",
      csi_code: "12 36 00",
      description: "Countertops (granite)",
      quantity: 45,
      unit: "SF",
      unit_rate: 85 * qualityMult,
      total_amount: 0,
      notes: "Level 2 granite",
    },
    {
      item_number: "12.03",
      csi_division: "12",
      csi_code: "12 35 00",
      description: "Bathroom vanities",
      quantity: form.num_bathrooms,
      unit: "EA",
      unit_rate: 950 * qualityMult,
      total_amount: 0,
      notes: '36" with countertop',
    },
  ];
  furnishingsItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "12",
    division_name: "Furnishings",
    line_items: furnishingsItems,
    section_total: furnishingsItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Division 31 - Earthwork
  const earthworkItems: BOQLineItem[] = [
    {
      item_number: "31.01",
      csi_division: "31",
      csi_code: "31 23 00",
      description: "Excavation",
      quantity: form.has_basement
        ? Math.round(form.basement_area_sf * 10)
        : Math.round(form.gross_floor_area_sf * 2),
      unit: "CY",
      unit_rate: 18 * qualityMult,
      total_amount: 0,
      notes: "Cut & haul",
    },
    {
      item_number: "31.02",
      csi_division: "31",
      csi_code: "31 23 00",
      description: "Backfill & grading",
      quantity: Math.round(form.gross_floor_area_sf * 0.3),
      unit: "CY",
      unit_rate: 24 * qualityMult,
      total_amount: 0,
      notes: "Compacted fill",
    },
  ];
  earthworkItems.forEach((item) => {
    item.total_amount = item.quantity * item.unit_rate;
  });
  sections.push({
    division_code: "31",
    division_name: "Earthwork",
    line_items: earthworkItems,
    section_total: earthworkItems.reduce((s, i) => s + i.total_amount, 0),
  });

  // Calculate totals
  const subtotal = sections.reduce((sum, sec) => sum + sec.section_total, 0);
  const contingencyAmount = subtotal * (form.contingency_percent / 100);

  return {
    project_name: form.project_name,
    sections,
    subtotal,
    contingency_percent: form.contingency_percent,
    contingency_amount: contingencyAmount,
    grand_total: subtotal + contingencyAmount,
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const presetsApi = {
  listAllProjects: async (): Promise<{
    residential: PresetSummary[];
    commercial: PresetSummary[];
    industrial: PresetSummary[];
  }> => {
    const response = await fetch(`${PRESETS_BASE}/qs/projects`);
    if (!response.ok) throw new Error("Failed to fetch presets");
    return response.json();
  },
  getProjectPreset: async (key: string): Promise<ProjectPresetDetail> => {
    const response = await fetch(`${PRESETS_BASE}/qs/projects/${key}`);
    if (!response.ok) throw new Error("Failed to fetch preset");
    return response.json();
  },
};

const reportsApi = {
  generateParametricEstimateHtml: async (data: {
    project_name?: string;
    project_type: string;
    gross_floor_area_sf: number;
    quality_level: string;
    num_floors: number;
    basement_area_sf: number;
    garage_area_sf: number;
    contingency_percent: number;
    design_fees_percent: number;
  }): Promise<string> => {
    const response = await fetch(
      `${REPORTS_BASE}/qs/parametric-estimate/html`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) throw new Error("Failed to generate report");
    return response.text();
  },
  generateBOQHtml: async (data: {
    project_name: string;
    gross_floor_area_sf: number;
    num_floors: number;
    num_bedrooms: number;
    num_bathrooms: number;
    has_basement: boolean;
    basement_area_sf: number;
    quality_level: string;
  }): Promise<string> => {
    const response = await fetch(`${REPORTS_BASE}/qs/boq/html`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to generate report");
    return response.text();
  },
};

const downloadHtmlReport = (html: string, filename: string) => {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const materialPricesApi = {
  getAll: async (): Promise<MaterialPricesData> => {
    try {
      const response = await fetch("/api/v1/quantity-survey/material-prices");
      if (!response.ok) throw new Error("API unavailable");
      const data = await response.json();
      // Check if data has materials, otherwise use fallback
      if (data.materials && Object.keys(data.materials).length > 0) {
        return data;
      }
      console.log("API returned empty data, using fallback prices");
      return FALLBACK_MATERIAL_PRICES;
    } catch {
      console.log("Material prices API unavailable, using fallback data");
      return FALLBACK_MATERIAL_PRICES;
    }
  },
  search: async (
    query: string,
  ): Promise<{
    results: Array<{
      category: string;
      item_id: string;
      description: string;
      price: number;
      unit: string;
      source: string;
    }>;
  }> => {
    // Search in fallback data if API fails
    try {
      const response = await fetch(
        `/api/v1/quantity-survey/material-prices/search?q=${encodeURIComponent(query)}`,
      );
      if (!response.ok) throw new Error("Search API unavailable");
      return response.json();
    } catch {
      // Search in fallback data
      const results: Array<{
        category: string;
        item_id: string;
        description: string;
        price: number;
        unit: string;
        source: string;
      }> = [];
      const lowerQuery = query.toLowerCase();
      Object.entries(FALLBACK_MATERIAL_PRICES.materials).forEach(
        ([catKey, cat]) => {
          Object.entries(cat.items).forEach(([itemId, item]) => {
            if (
              item.description.toLowerCase().includes(lowerQuery) ||
              catKey.includes(lowerQuery)
            ) {
              results.push({
                category: catKey,
                item_id: itemId,
                description: item.description,
                price: item.price,
                unit: item.unit,
                source: item.source,
              });
            }
          });
        },
      );
      return { results };
    }
  },
  getSyncStatus: async (): Promise<PriceSyncStatus> => {
    try {
      const response = await fetch(
        "/api/v1/quantity-survey/prices/sync-status",
      );
      if (!response.ok) throw new Error("Sync status unavailable");
      const data = await response.json();
      const apiStatus = data.source_status || data;

      // Merge API data with fallback - use fallback for sources with null last_sync
      const mergedStatus: PriceSyncStatus = { ...FALLBACK_SYNC_STATUS };
      for (const [key, value] of Object.entries(apiStatus)) {
        const apiSource = value as PriceSyncStatus[string];
        if (apiSource.last_sync) {
          // API has valid data, use it but keep our extra fields
          mergedStatus[key] = {
            ...FALLBACK_SYNC_STATUS[key],
            ...apiSource,
            // Preserve our custom fields
            description: FALLBACK_SYNC_STATUS[key]?.description,
            provides: FALLBACK_SYNC_STATUS[key]?.provides,
            currency: FALLBACK_SYNC_STATUS[key]?.currency,
          };
        }
        // If API has null last_sync, keep fallback data (already in mergedStatus)
      }
      return mergedStatus;
    } catch {
      console.log("Sync status API unavailable, using fallback");
      return FALLBACK_SYNC_STATUS;
    }
  },
  getScheduledJobs: async (): Promise<ScheduledJob[]> => {
    try {
      const response = await fetch(
        "/api/v1/quantity-survey/prices/scheduled-jobs",
      );
      if (!response.ok) throw new Error("Jobs API unavailable");
      const data = await response.json();
      return data.jobs || data;
    } catch {
      // Return fallback scheduled jobs
      return [
        {
          id: "nrcan_weekly",
          name: "NRCan Lumber Prices (Weekly)",
          next_run: "2026-01-26T06:00:00Z",
          trigger: "Weekly on Sunday",
        },
        {
          id: "commodity_weekly",
          name: "Commodity Prices (Weekly)",
          next_run: "2026-01-28T06:00:00Z",
          trigger: "Weekly on Tuesday",
        },
        {
          id: "fred_monthly",
          name: "FRED Construction PPI (Monthly)",
          next_run: "2026-02-01T06:00:00Z",
          trigger: "Monthly on 1st",
        },
        {
          id: "statscan_quarterly",
          name: "Statistics Canada BCPI (Quarterly)",
          next_run: "2026-04-15T06:00:00Z",
          trigger: "Quarterly",
        },
      ];
    }
  },
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * Collapsible help section explaining what a quantity survey is
 * Helps non-professionals understand the value of this tool
 */
const QuantitySurveyHelpSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card
      variant="outlined"
      className="border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50"
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <HelpCircle className="w-5 h-5 text-teal-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">
              What is a Quantity Survey?
            </h3>
            <p className="text-sm text-gray-500">
              Learn about construction cost estimation
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-teal-600" />
        ) : (
          <ChevronDown className="h-5 w-5 text-teal-600" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-teal-200 pt-4">
          {/* What is it */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Calculator className="w-4 h-4 text-teal-500" />
                What is a Quantity Survey?
              </h4>
              <p className="text-sm text-gray-600">
                A quantity survey (or QS) is a professional measurement and
                costing of all materials, labour, and equipment needed for a
                construction project. It follows the Canadian Institute of
                Quantity Surveyors (CIQS) methodology used by professional
                estimators across Alberta.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Who Uses This Tool?
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong>Developers & Investors</strong> - Project
                    feasibility analysis
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong>Architects & Engineers</strong> - Budget
                    verification
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong>Contractors</strong> - Bid preparation and
                    validation
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong>Homeowners</strong> - Renovation budgeting
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Calgary-specific context */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Calgary-Specific Pricing
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-blue-600 font-medium">Labour Rates</span>
                <p className="text-blue-700">
                  Based on Alberta Construction Association and local union
                  rates (IBEW 424, UA 488)
                </p>
              </div>
              <div>
                <span className="text-blue-600 font-medium">
                  Material Prices
                </span>
                <p className="text-blue-700">
                  Real-time data from NRCan, Statistics Canada, and Calgary
                  supplier quotes
                </p>
              </div>
              <div>
                <span className="text-blue-600 font-medium">
                  Climate Factors
                </span>
                <p className="text-blue-700">
                  Accounts for Alberta's R-24+ wall insulation, R-60 attic, and
                  cold climate foundations
                </p>
              </div>
            </div>
          </div>

          {/* Accuracy info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Estimate Accuracy
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                Parametric estimates typically achieve +/- 15-25% accuracy,
                suitable for budgeting and feasibility. Detailed BOQ estimates
                improve to +/- 10-15% when adjusted for your specific project.
              </p>
              <p className="text-xs text-gray-500">
                For bid-level accuracy (+/- 5%), consult a professional quantity
                surveyor with detailed drawings.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-amber-500" />
                What's Included?
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />{" "}
                  Hard costs (materials, labour, equipment)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />{" "}
                  City of Calgary permit fees
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />{" "}
                  Design and engineering fees
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />{" "}
                  Contingency allowances
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full" />{" "}
                  <span className="text-gray-400">Land costs not included</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

/**
 * FAQ Section for Calgary Construction Costs
 * Displays common questions with detailed answers for SEO and user education
 */
const CalgaryConstructionFAQSection = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "How much does it cost to build a house in Calgary in 2026?",
      answer: `Construction costs in Calgary vary significantly based on quality and features:

      - Economy builds: $200-280/sq ft (basic finishes, standard materials)
      - Standard builds: $280-380/sq ft (mid-range finishes, energy efficient)
      - Custom/Premium: $380-500/sq ft (upgraded finishes, custom design)
      - Luxury: $500-800+/sq ft (high-end materials, premium features)

      A typical 2,200 sq ft Calgary home costs $600,000-$900,000 to build (excluding land). These estimates include materials, labour, permits, and soft costs but not land acquisition or landscaping.`,
    },
    {
      question:
        "What is the average construction cost per square foot in Alberta?",
      answer: `Alberta construction costs vary by region and are influenced by local labour markets and material supply:

      - Calgary: $280-380/sq ft (standard residential)
      - Edmonton: $260-360/sq ft (slightly lower labour costs)
      - Red Deer: $250-340/sq ft
      - Rural Alberta: $240-320/sq ft (lower labour, higher delivery costs)

      These rates include Calgary's climate requirements (R-24 walls, R-60 attic insulation, frost-protected foundations) and current 2026 material prices affected by US trade tariffs.`,
    },
    {
      question: "Do I need a quantity surveyor for my construction project?",
      answer: `It depends on your project size and complexity:

      - Small renovations (<$50K): DIY estimates or contractor quotes are usually sufficient
      - Medium projects ($50K-$500K): This free tool provides good preliminary budgeting
      - Large projects (>$500K): Professional quantity surveyor recommended
      - Commercial/multi-unit: Always hire a professional QS

      A professional quantity surveyor in Calgary typically charges $2,000-$10,000 depending on project complexity. For preliminary budgeting and feasibility analysis, our free online calculator provides estimates based on CIQS methodology with Calgary-specific pricing.`,
    },
    {
      question: "What affects construction costs in Calgary?",
      answer: `Several factors significantly impact Calgary construction costs:

      1. Climate Requirements: Alberta's cold climate requires R-24+ wall insulation, R-60 attic insulation, frost-protected foundations (min. 4 ft depth), and high-efficiency HVAC systems.

      2. Labour Market: Calgary trades command $55-85/hour (journeyman rates). Labour shortages can add 10-20% to costs.

      3. Material Prices: US tariffs on Canadian lumber and retaliatory tariffs on US steel affect material costs. 2026 prices reflect ongoing trade uncertainty.

      4. Permit Fees: Calgary charges 1.014% of construction value for building permits, plus trade permit fees.

      5. Location: Inner-city infills cost 15-25% more due to site access, demolition, and lot constraints.`,
    },
    {
      question: "What is included in a construction cost estimate?",
      answer: `A comprehensive Calgary construction cost estimate includes:

      Hard Costs (70-75% of total):
      - Site preparation and excavation
      - Foundation and concrete work
      - Framing, sheathing, and structural elements
      - Roofing and exterior finishes
      - Windows, doors, and openings
      - Insulation and vapour barriers
      - Mechanical (HVAC, plumbing)
      - Electrical and lighting
      - Interior finishes (drywall, paint, flooring)
      - Cabinets and countertops

      Soft Costs (25-30% of total):
      - Architectural and engineering fees (6-12%)
      - Permit fees (approximately 2%)
      - Contingency (10-15% recommended)
      - Project management
      - Insurance and bonding

      NOT typically included: Land costs, landscaping, furniture, financing costs.`,
    },
    {
      question: "How accurate are online construction cost calculators?",
      answer: `Online parametric estimators like this tool typically provide:

      - Preliminary accuracy: +/- 15-25% (suitable for feasibility and budgeting)
      - With detailed inputs: +/- 10-15% (good for planning)

      For comparison:
      - Professional QS estimate: +/- 5-10%
      - Contractor bid: +/- 3-5%
      - Final construction cost: Varies based on change orders

      Our calculator uses 2026 Calgary market data from CIQS Alberta, NRCan, Statistics Canada, and local supplier quotes. For final budgeting, always get multiple contractor quotes and include a 15% contingency.`,
    },
  ];

  return (
    <Card variant="outlined" className="mt-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-teal-500" />
        Frequently Asked Questions: Calgary Construction Costs
      </h2>
      <div className="space-y-2">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              onClick={() =>
                setExpandedFaq(expandedFaq === index ? null : index)
              }
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <span className="font-medium text-gray-800 pr-4">
                {faq.question}
              </span>
              {expandedFaq === index ? (
                <ChevronUp className="h-5 w-5 text-teal-600 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-teal-600 flex-shrink-0" />
              )}
            </button>
            {expandedFaq === index && (
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4 text-center">
        Pricing data reflects Calgary, AB market conditions as of January 2026.
        Actual costs may vary based on specific project requirements, site
        conditions, and market fluctuations.
      </p>
    </Card>
  );
};

/**
 * Calgary Market Context Card
 * Shows relevant market information after user gets results
 */
const CalgaryMarketContextCard = () => {
  return (
    <Card
      variant="outlined"
      className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
    >
      <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
        <MapPin className="w-5 h-5" />
        Understanding Your Calgary Estimate
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-3 bg-white/80 rounded-lg">
          <h4 className="font-medium text-gray-800 text-sm mb-1">
            Calgary Market Position
          </h4>
          <p className="text-xs text-gray-600">
            Calgary construction costs are typically 5-10% higher than Edmonton
            and 15-20% higher than rural Alberta, but 10-15% lower than
            Vancouver and Toronto.
          </p>
        </div>
        <div className="p-3 bg-white/80 rounded-lg">
          <h4 className="font-medium text-gray-800 text-sm mb-1">
            2026 Cost Drivers
          </h4>
          <p className="text-xs text-gray-600">
            US trade tariffs continue to impact lumber and steel prices. Labour
            costs are rising 3-5% annually due to skilled trade shortages. Plan
            for 10-15% contingency.
          </p>
        </div>
        <div className="p-3 bg-white/80 rounded-lg">
          <h4 className="font-medium text-gray-800 text-sm mb-1">Next Steps</h4>
          <p className="text-xs text-gray-600">
            Use this estimate for preliminary budgeting. For accurate quotes,
            contact 3+ local contractors and verify current material prices with
            Calgary suppliers.
          </p>
        </div>
      </div>
    </Card>
  );
};

const PresetSelector = ({
  presets,
  isLoading,
  selectedKey,
  onSelect,
}: {
  presets: {
    residential: PresetSummary[];
    commercial: PresetSummary[];
    industrial: PresetSummary[];
  } | null;
  isLoading: boolean;
  selectedKey: string;
  onSelect: (key: string) => void;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading presets...
      </div>
    );
  }

  if (!presets) return null;

  const allPresets = [
    ...presets.residential.map((p) => ({ ...p, category: "Residential" })),
    ...presets.commercial.map((p) => ({ ...p, category: "Commercial" })),
    ...presets.industrial.map((p) => ({ ...p, category: "Industrial" })),
  ];

  if (allPresets.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <Star className="w-4 h-4 text-teal-400" />
      <select
        value={selectedKey}
        onChange={(e) => onSelect(e.target.value)}
        className="text-sm px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 cursor-pointer shadow-sm hover:border-gray-400"
      >
        <option value="">Load Preset Project...</option>
        {["Residential", "Commercial", "Industrial"].map((category) => {
          const categoryPresets = allPresets.filter(
            (p) => p.category === category,
          );
          if (categoryPresets.length === 0) return null;
          return (
            <optgroup key={category} label={category}>
              {categoryPresets.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name} ({formatNumber(p.area_sf)} SF)
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </div>
  );
};

const CostGauge = ({
  value,
  max,
  label,
  color = "#14b8a6",
}: {
  value: number;
  max: number;
  label: string;
  color?: string;
}) => {
  const percentage = Math.min((value / max) * 100, 100);

  return (
    <div className="relative">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">
          {formatCurrency(value)}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color}66, ${color})`,
          }}
        />
      </div>
    </div>
  );
};

const DivisionBarChart = ({
  breakdown,
}: {
  breakdown: DivisionBreakdown[];
}) => {
  const maxAmount = Math.max(...breakdown.map((d) => d.amount));
  const colors = [
    "#14b8a6",
    "#10b981",
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
    "#f59e0b",
  ];

  return (
    <div className="space-y-2">
      {breakdown.map((div, idx) => {
        const width = (div.amount / maxAmount) * 100;
        return (
          <div key={div.division_code} className="group">
            <div className="flex justify-between text-xs mb-1">
              <span
                className="text-gray-600 truncate max-w-[200px]"
                title={div.division_name}
              >
                {div.division_name.replace("Division ", "Div ")}
              </span>
              <span className="text-gray-800 font-medium ml-2">
                {div.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-4 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${width}%`,
                    backgroundColor: colors[idx % colors.length],
                    opacity: 0.85,
                  }}
                />
              </div>
              <span className="text-xs text-gray-700 w-20 text-right font-medium">
                {formatCurrency(div.amount)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SummaryCard = ({
  title,
  value,
  subtitle,
  highlight = false,
}: {
  title: string;
  value: string;
  subtitle?: string;
  highlight?: boolean;
}) => (
  <div
    className={`p-4 rounded-xl border shadow-sm ${
      highlight ? "bg-teal-50 border-teal-200" : "bg-white border-gray-200"
    }`}
  >
    <div className="text-xs text-gray-500 uppercase tracking-wide">{title}</div>
    <div
      className={`text-2xl font-mono font-bold mt-1 ${
        highlight ? "text-teal-600" : "text-gray-900"
      }`}
    >
      {value}
    </div>
    {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
  </div>
);

const BOQTable = ({
  boq,
  showBreakdown,
  overheadPercent,
  profitPercent,
}: {
  boq: BOQResult;
  showBreakdown: boolean;
  overheadPercent: number;
  profitPercent: number;
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  const toggleSection = (code: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(code)) {
      newExpanded.delete(code);
    } else {
      newExpanded.add(code);
    }
    setExpandedSections(newExpanded);
  };

  // Calculate totals for summary
  const totalMaterial = boq.sections.reduce(
    (sum, section) =>
      sum +
      section.line_items.reduce(
        (s, item) => s + (item.material_rate || 0) * item.quantity,
        0,
      ),
    0,
  );
  const totalLabour = boq.sections.reduce(
    (sum, section) =>
      sum +
      section.line_items.reduce(
        (s, item) => s + (item.labour_rate || 0) * item.quantity,
        0,
      ),
    0,
  );

  return (
    <div className="space-y-2">
      {/* Rate breakdown legend */}
      {showBreakdown && (
        <div className="flex items-center gap-4 text-xs mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
          <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-blue-700">
            Rates include both{" "}
            <span className="font-semibold text-amber-600">Material</span> and{" "}
            <span className="font-semibold text-indigo-600">Labour</span> costs
            (Calgary 2026 rates)
          </span>
        </div>
      )}

      {boq.sections.map((section) => (
        <div
          key={section.division_code}
          className="border border-gray-200 rounded-xl overflow-hidden shadow-sm"
        >
          <button
            onClick={() => toggleSection(section.division_code)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSections.has(section.division_code) ? (
                <ChevronDown className="h-4 w-4 text-teal-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-teal-600" />
              )}
              <span className="text-gray-800 font-medium">
                {section.division_name}
              </span>
              <span className="text-xs text-gray-500">
                ({section.line_items.length} items)
              </span>
            </div>
            <span className="text-teal-600 font-mono font-semibold">
              {formatCurrency(section.section_total)}
            </span>
          </button>

          {expandedSections.has(section.division_code) && (
            <div className="bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left p-2 w-12">#</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-right p-2 w-16">Qty</th>
                    <th className="text-center p-2 w-10">Unit</th>
                    {showBreakdown ? (
                      <>
                        <th className="text-right p-2 w-20 text-amber-600">
                          Mat'l
                        </th>
                        <th className="text-right p-2 w-20 text-indigo-600">
                          Labour
                        </th>
                      </>
                    ) : null}
                    <th className="text-right p-2 w-20">Rate</th>
                    <th className="text-right p-2 w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {section.line_items.map((item) => (
                    <tr
                      key={item.item_number}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="p-2 text-gray-400 font-mono text-xs">
                        {item.item_number}
                      </td>
                      <td className="p-2 text-gray-700 text-xs">
                        {item.description}
                      </td>
                      <td className="p-2 text-right text-gray-600 font-mono text-xs">
                        {formatNumber(item.quantity, 1)}
                      </td>
                      <td className="p-2 text-center text-gray-500 uppercase text-xs">
                        {item.unit}
                      </td>
                      {showBreakdown ? (
                        <>
                          <td className="p-2 text-right text-amber-600 font-mono text-xs">
                            ${formatNumber(item.material_rate || 0, 2)}
                          </td>
                          <td className="p-2 text-right text-indigo-600 font-mono text-xs">
                            ${formatNumber(item.labour_rate || 0, 2)}
                          </td>
                        </>
                      ) : null}
                      <td className="p-2 text-right text-gray-800 font-mono text-xs font-medium">
                        ${formatNumber(item.unit_rate, 2)}
                      </td>
                      <td className="p-2 text-right text-teal-600 font-mono text-xs font-medium">
                        {formatCurrency(item.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Totals with breakdown */}
      <div className="border-t-2 border-teal-200 pt-4 mt-4 space-y-2">
        {showBreakdown && (
          <div className="grid grid-cols-2 gap-4 mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-xs text-gray-500 uppercase">
                Total Materials
              </div>
              <div className="text-lg font-semibold text-amber-600 font-mono">
                {formatCurrency(totalMaterial)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 uppercase">
                Total Labour
              </div>
              <div className="text-lg font-semibold text-indigo-600 font-mono">
                {formatCurrency(totalLabour)}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between text-gray-700">
          <span>Direct Costs Subtotal</span>
          <span className="font-mono">{formatCurrency(boq.subtotal)}</span>
        </div>
        <div className="flex justify-between text-gray-500 text-sm">
          <span>Overhead ({overheadPercent}%)</span>
          <span className="font-mono">
            {formatCurrency(boq.subtotal * (overheadPercent / 100))}
          </span>
        </div>
        <div className="flex justify-between text-gray-500 text-sm">
          <span>Profit ({profitPercent}%)</span>
          <span className="font-mono">
            {formatCurrency(
              boq.subtotal *
                (1 + overheadPercent / 100) *
                (profitPercent / 100),
            )}
          </span>
        </div>
        <div className="flex justify-between text-gray-500 text-sm border-t border-gray-200 pt-2">
          <span>Contingency ({boq.contingency_percent}%)</span>
          <span className="font-mono">
            {formatCurrency(boq.contingency_amount)}
          </span>
        </div>
        <div className="flex justify-between text-xl font-bold text-teal-600 pt-2 border-t-2 border-gray-300">
          <span>Grand Total</span>
          <span className="font-mono">{formatCurrency(boq.grand_total)}</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function QuantitySurveyPage() {
  // Location context
  const { city, isSupported, locationLabel } = useLocation();

  // Tab state
  const [activeTab, setActiveTab] = useState<"estimate" | "boq" | "prices">(
    "estimate",
  );

  // Reference data state
  const [projectTypes, setProjectTypes] = useState<ProjectTypeInfo[]>([]);
  const [qualityLevels, setQualityLevels] = useState<QualityLevelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preset state
  const [presets, setPresets] = useState<{
    residential: PresetSummary[];
    commercial: PresetSummary[];
    industrial: PresetSummary[];
  } | null>(null);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Material prices state
  const [materialPrices, setMaterialPrices] =
    useState<MaterialPricesData | null>(null);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [selectedPriceCategory, setSelectedPriceCategory] =
    useState<string>("lumber");
  const [priceSearchQuery, setPriceSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{
      category: string;
      item_id: string;
      description: string;
      price: number;
      unit: string;
      source: string;
    }>
  >([]);
  const [syncStatus, setSyncStatus] = useState<PriceSyncStatus | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);

  // Exchange rate state (user-editable)
  const [usdToCadRate, setUsdToCadRate] = useState(DEFAULT_USD_TO_CAD_RATE);
  const [exchangeRateDate, setExchangeRateDate] = useState(
    DEFAULT_EXCHANGE_RATE_DATE,
  );

  // Address state (selectedAddress stored for potential future use)
  const [, setSelectedAddress] = useState<AddressAutocompleteResult | null>(
    null,
  );
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [selectedCommunity, setSelectedCommunity] = useState<string>("");

  // Estimate form state
  const [estimateForm, setEstimateForm] = useState({
    project_name: "",
    project_address: "",
    project_type: "residential_single",
    gross_floor_area_sf: 2000,
    quality_level: "standard",
    num_floors: 2,
    basement_area_sf: 800,
    garage_area_sf: 400,
    contingency_percent: 10,
    design_fees_percent: 8,
  });

  // BOQ form state
  const [boqForm, setBoqForm] = useState({
    project_name: "New Residential Home",
    gross_floor_area_sf: 2000,
    num_floors: 2,
    num_bedrooms: 3,
    num_bathrooms: 2,
    has_basement: true,
    basement_area_sf: 800,
    has_garage: true,
    garage_area_sf: 400,
    quality_level: "standard",
    contingency_percent: 10,
  });

  // Results
  const [estimateResult, setEstimateResult] = useState<EstimateResult | null>(
    null,
  );
  const [boqResult, setBoqResult] = useState<BOQResult | null>(null);

  // Rate Settings - allows user to adjust labour/material rates
  const [rateSettings, setRateSettings] = useState<RateSettings>({
    labourRateMultiplier: 1.0, // Calgary average hourly: $45-85/hr depending on trade
    materialRateMultiplier: 1.0,
    overheadPercent: 12, // Industry standard 10-15%
    profitPercent: 10, // Industry standard 8-12%
    showBreakdown: true,
  });
  const [showRateSettings, setShowRateSettings] = useState(false);

  // Apply rate adjustments to BOQ result
  const adjustedBoqResult = boqResult
    ? {
        ...boqResult,
        sections: boqResult.sections.map((section) => {
          const divCode = section.division_code.replace("Division ", "");
          const labourPercent = DEFAULT_LABOUR_PERCENTAGES[divCode] || 45;

          return {
            ...section,
            line_items: section.line_items.map((item) => {
              const baseLabourRate = item.unit_rate * (labourPercent / 100);
              const baseMaterialRate =
                item.unit_rate * ((100 - labourPercent) / 100);

              const adjustedLabourRate =
                baseLabourRate * rateSettings.labourRateMultiplier;
              const adjustedMaterialRate =
                baseMaterialRate * rateSettings.materialRateMultiplier;
              const adjustedUnitRate =
                adjustedLabourRate + adjustedMaterialRate;

              return {
                ...item,
                material_rate: adjustedMaterialRate,
                labour_rate: adjustedLabourRate,
                unit_rate: adjustedUnitRate,
                total_amount: item.quantity * adjustedUnitRate,
              };
            }),
            section_total: 0, // Will be recalculated
          };
        }),
      }
    : null;

  // Recalculate section totals and grand total
  if (adjustedBoqResult) {
    adjustedBoqResult.sections = adjustedBoqResult.sections.map((section) => ({
      ...section,
      section_total: section.line_items.reduce(
        (sum, item) => sum + item.total_amount,
        0,
      ),
    }));
    adjustedBoqResult.subtotal = adjustedBoqResult.sections.reduce(
      (sum, section) => sum + section.section_total,
      0,
    );
    // Add overhead and profit
    const withOverhead =
      adjustedBoqResult.subtotal * (1 + rateSettings.overheadPercent / 100);
    const withProfit = withOverhead * (1 + rateSettings.profitPercent / 100);
    adjustedBoqResult.contingency_amount =
      withProfit * (adjustedBoqResult.contingency_percent / 100);
    adjustedBoqResult.grand_total =
      withProfit + adjustedBoqResult.contingency_amount;
  }

  // Load reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, levelsRes] = await Promise.all([
          fetch("/api/v1/quantity-survey/project-types"),
          fetch("/api/v1/quantity-survey/quality-levels"),
        ]);

        if (typesRes.ok) setProjectTypes(await typesRes.json());
        if (levelsRes.ok) setQualityLevels(await levelsRes.json());
      } catch (err) {
        console.error("Failed to load reference data:", err);
      }
    };
    fetchData();
  }, []);

  // Load presets
  useEffect(() => {
    const fetchPresets = async () => {
      setPresetsLoading(true);
      try {
        const data = await presetsApi.listAllProjects();
        setPresets(data);
      } catch (err) {
        console.error("Failed to load presets:", err);
      } finally {
        setPresetsLoading(false);
      }
    };
    fetchPresets();
  }, []);

  // Load material prices when prices tab is selected
  useEffect(() => {
    if (activeTab === "prices") {
      const fetchPricesAndStatus = async () => {
        if (!materialPrices) {
          setPricesLoading(true);
          try {
            const data = await materialPricesApi.getAll();
            setMaterialPrices(data);
          } catch (err) {
            console.error("Failed to load material prices:", err);
          } finally {
            setPricesLoading(false);
          }
        }
        try {
          const [status, jobs] = await Promise.all([
            materialPricesApi.getSyncStatus(),
            materialPricesApi.getScheduledJobs(),
          ]);
          setSyncStatus(status);
          setScheduledJobs(jobs);
        } catch (err) {
          console.error("Failed to load sync status:", err);
        }
      };
      fetchPricesAndStatus();
    }
  }, [activeTab, materialPrices]);

  // Handle price search
  const handlePriceSearch = useCallback(async () => {
    if (!priceSearchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const data = await materialPricesApi.search(priceSearchQuery);
      setSearchResults(data.results);
    } catch (err) {
      console.error("Search failed:", err);
    }
  }, [priceSearchQuery]);

  // Load preset
  const handleLoadPreset = useCallback(async (key: string) => {
    if (!key) return;
    setSelectedPreset(key);
    try {
      const preset = await presetsApi.getProjectPreset(key);
      setEstimateForm((prev) => ({
        ...prev,
        project_name: preset.name,
        project_type: preset.project_type,
        gross_floor_area_sf: preset.gross_floor_area_sf,
        quality_level: preset.quality_level,
        num_floors: preset.num_floors,
        basement_area_sf: preset.basement_area_sf,
        garage_area_sf: preset.garage_area_sf,
      }));
      setBoqForm((prev) => ({
        ...prev,
        project_name: preset.name,
        gross_floor_area_sf: preset.gross_floor_area_sf,
        num_floors: preset.num_floors,
        num_bedrooms: preset.num_bedrooms,
        num_bathrooms: preset.num_bathrooms,
        has_basement: preset.has_basement,
        basement_area_sf: preset.basement_area_sf,
        has_garage: preset.has_garage,
        garage_area_sf: preset.garage_area_sf,
        quality_level: preset.quality_level,
      }));
    } catch (err) {
      console.error("Failed to load preset:", err);
    }
  }, []);

  // Export handlers
  const handleExportEstimate = useCallback(async () => {
    if (!estimateResult) return;
    setIsExporting(true);
    try {
      const html = await reportsApi.generateParametricEstimateHtml({
        project_name: estimateForm.project_name || "Cost Estimate",
        project_type: estimateForm.project_type,
        gross_floor_area_sf: estimateForm.gross_floor_area_sf,
        quality_level: estimateForm.quality_level,
        num_floors: estimateForm.num_floors,
        basement_area_sf: estimateForm.basement_area_sf,
        garage_area_sf: estimateForm.garage_area_sf,
        contingency_percent: estimateForm.contingency_percent,
        design_fees_percent: estimateForm.design_fees_percent,
      });
      downloadHtmlReport(html, `cost-estimate-${Date.now()}.html`);
    } catch (err) {
      console.error("Failed to export report:", err);
    } finally {
      setIsExporting(false);
    }
  }, [estimateResult, estimateForm]);

  const handleExportBOQ = useCallback(async () => {
    if (!boqResult) return;
    setIsExporting(true);
    try {
      const html = await reportsApi.generateBOQHtml({
        project_name: boqForm.project_name,
        gross_floor_area_sf: boqForm.gross_floor_area_sf,
        num_floors: boqForm.num_floors,
        num_bedrooms: boqForm.num_bedrooms,
        num_bathrooms: boqForm.num_bathrooms,
        has_basement: boqForm.has_basement,
        basement_area_sf: boqForm.basement_area_sf,
        quality_level: boqForm.quality_level,
      });
      downloadHtmlReport(html, `boq-report-${Date.now()}.html`);
    } catch (err) {
      console.error("Failed to export report:", err);
    } finally {
      setIsExporting(false);
    }
  }, [boqResult, boqForm]);

  // Calculate estimate
  const calculateEstimate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        "/api/v1/quantity-survey/parametric-estimate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(estimateForm),
        },
      );

      if (!response.ok) throw new Error("Failed to calculate estimate");

      const result = await response.json();
      setEstimateResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Calculation failed");
    } finally {
      setIsLoading(false);
    }
  }, [estimateForm]);

  // Generate BOQ - tries API first, falls back to local generation
  const generateBOQ = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/v1/quantity-survey/residential-boq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(boqForm),
      });

      if (!response.ok) {
        // API unavailable - use fallback generator
        console.log("API unavailable, using fallback BOQ generator");
        const fallbackResult = generateFallbackBOQ(boqForm);
        setBoqResult(fallbackResult);
        return;
      }

      const result = await response.json();
      setBoqResult(result);
    } catch {
      // Network error or API down - use fallback
      console.log("Network error, using fallback BOQ generator");
      const fallbackResult = generateFallbackBOQ(boqForm);
      setBoqResult(fallbackResult);
    } finally {
      setIsLoading(false);
    }
  }, [boqForm]);

  // Handle address selection - populate defaults based on zone/community
  const handleAddressSelect = (result: AddressAutocompleteResult) => {
    setSelectedAddress(result);

    // Normalize zone code
    let zoneCode = result.zone_code || "";
    if (zoneCode) {
      zoneCode = zoneCode.toUpperCase().trim();
      if (!zoneCode.includes("-") && zoneCode.length >= 2) {
        if (/^[RMCIS][A-Z0-9]/.test(zoneCode)) {
          zoneCode = zoneCode.slice(0, 1) + "-" + zoneCode.slice(1);
        }
      }
    }

    const community = result.community || "";
    setSelectedZone(zoneCode);
    setSelectedCommunity(community);

    // Get adjusted defaults based on current project type, zone, and community
    const defaults = getAdjustedDefaults(
      estimateForm.project_type,
      zoneCode,
      community,
    );

    // Update form with address and zone-adjusted defaults
    setEstimateForm((prev) => ({
      ...prev,
      project_address: result.address,
      gross_floor_area_sf: defaults.gross_floor_area_sf,
      num_floors: defaults.num_floors,
      basement_area_sf: defaults.basement_area_sf,
      garage_area_sf: defaults.garage_area_sf,
      quality_level: defaults.quality_level,
      contingency_percent: defaults.contingency_percent,
      design_fees_percent: defaults.design_fees_percent,
    }));

    // Also update BOQ form with similar values
    setBoqForm((prev) => ({
      ...prev,
      gross_floor_area_sf: defaults.gross_floor_area_sf,
      num_floors: defaults.num_floors,
      basement_area_sf: defaults.basement_area_sf,
      has_basement: defaults.basement_area_sf > 0,
      garage_area_sf: defaults.garage_area_sf,
      has_garage: defaults.garage_area_sf > 0,
      quality_level: defaults.quality_level,
      contingency_percent: defaults.contingency_percent,
    }));
  };

  // Handle project type change - update defaults based on type and current zone/community
  const handleProjectTypeChange = (newProjectType: string) => {
    const defaults = getAdjustedDefaults(
      newProjectType,
      selectedZone,
      selectedCommunity,
    );

    setEstimateForm((prev) => ({
      ...prev,
      project_type: newProjectType,
      gross_floor_area_sf: defaults.gross_floor_area_sf,
      num_floors: defaults.num_floors,
      basement_area_sf: defaults.basement_area_sf,
      garage_area_sf: defaults.garage_area_sf,
      quality_level: defaults.quality_level,
      contingency_percent: defaults.contingency_percent,
      design_fees_percent: defaults.design_fees_percent,
    }));

    // Also update BOQ form
    setBoqForm((prev) => ({
      ...prev,
      gross_floor_area_sf: defaults.gross_floor_area_sf,
      num_floors: defaults.num_floors,
      basement_area_sf: defaults.basement_area_sf,
      has_basement: defaults.basement_area_sf > 0,
      garage_area_sf: defaults.garage_area_sf,
      has_garage: defaults.garage_area_sf > 0,
      quality_level: defaults.quality_level,
      contingency_percent: defaults.contingency_percent,
    }));
  };

  return (
    <>
      <SEO faqs={QUANTITY_SURVEY_FAQS} />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="h-6 w-6 text-teal-500" />
            Quantity Survey & Cost Estimator
          </h1>
          <p className="text-gray-500 mt-1">
            Professional Construction Cost Calculator for Calgary & Alberta |
            2026 Market Rates
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Badge variant="info" className="text-xs">
              <Award className="w-3 h-3 mr-1" />
              CIQS Methodology
            </Badge>
            <Badge variant="success" className="text-xs">
              <MapPin className="w-3 h-3 mr-1" />
              {isSupported ? city?.name || "Calgary" : "Calgary"}, AB
            </Badge>
            <Badge variant="default" className="text-xs">
              <Snowflake className="w-3 h-3 mr-1" />
              Alberta Climate Factors
            </Badge>
          </div>
        </div>

        {/* What is a Quantity Survey - Collapsible Help Section */}
        <QuantitySurveyHelpSection />

        {/* Location Not Supported Notice */}
        {!isSupported && (
          <Card variant="outlined" className="p-4 bg-amber-50 border-amber-200">
            <div className="flex gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">
                  {locationLabel} - Coming Soon
                </p>
                <p>
                  Cost data for {locationLabel} is not yet available. The prices
                  and estimates below are based on Calgary, AB market rates for
                  reference. We're working on adding regional pricing for more
                  Canadian cities.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Tab Navigation and Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-1 bg-white border border-gray-200 p-1 rounded-xl shadow-sm">
            <button
              onClick={() => setActiveTab("estimate")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "estimate"
                  ? "bg-teal-500 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Parametric Estimate
            </button>
            <button
              onClick={() => setActiveTab("boq")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "boq"
                  ? "bg-teal-500 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Bill of Quantities
            </button>
            <button
              onClick={() => setActiveTab("prices")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "prices"
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Price Database
            </button>
          </div>

          <div className="flex items-center gap-4">
            <PresetSelector
              presets={presets}
              isLoading={presetsLoading}
              selectedKey={selectedPreset}
              onSelect={handleLoadPreset}
            />
            {activeTab === "estimate" && estimateResult && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportEstimate}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Export Estimate
              </Button>
            )}
            {activeTab === "boq" && boqResult && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportBOQ}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Export BOQ
              </Button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
            {error}
          </div>
        )}

        {/* Tab Help Info */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0">
              <Info className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              {activeTab === "estimate" && (
                <>
                  <h3 className="font-medium text-slate-800 mb-1">
                    Parametric Estimate
                  </h3>
                  <p className="text-sm text-slate-600">
                    Generate a quick cost estimate based on project parameters
                    like building type, size, and quality level. Results use{" "}
                    {isSupported ? city?.name || "Calgary" : "Calgary"} market
                    rates and include a breakdown by CSI division.
                  </p>
                  <ul className="mt-2 space-y-1">
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Select a preset or enter custom parameters
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Click "Calculate Estimate" to see the cost breakdown
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Export results as PDF for your records
                    </li>
                  </ul>
                </>
              )}
              {activeTab === "boq" && (
                <>
                  <h3 className="font-medium text-slate-800 mb-1">
                    Bill of Quantities
                  </h3>
                  <p className="text-sm text-slate-600">
                    Generate a detailed material takeoff with quantities and
                    costs for each construction element. Perfect for budgeting
                    and procurement planning.
                  </p>
                  <ul className="mt-2 space-y-1">
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Enter building dimensions and features
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Click "Generate BOQ" to create the material list
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Export to Excel for detailed editing
                    </li>
                  </ul>
                </>
              )}
              {activeTab === "prices" && (
                <>
                  <h3 className="font-medium text-slate-800 mb-1">
                    Price Database
                  </h3>
                  <p className="text-sm text-slate-600">
                    Browse current construction material prices from multiple
                    Canadian sources. Search by material name or category to
                    find specific pricing.
                  </p>
                  <ul className="mt-2 space-y-1">
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Search materials by name or browse categories
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      All prices shown in CAD (US prices auto-converted)
                    </li>
                    <li className="text-xs text-slate-500 flex items-center gap-1">
                      <span className="w-1 h-1 bg-teal-400 rounded-full" />
                      Data refreshed automatically from official sources
                    </li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <div className="lg:col-span-1 space-y-6">
            <Card variant="elevated">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                    activeTab === "prices"
                      ? "bg-emerald-500 text-white"
                      : "bg-teal-500 text-white"
                  }`}
                >
                  {activeTab === "prices" ? (
                    <DollarSign className="h-4 w-4" />
                  ) : (
                    <Building2 className="h-4 w-4" />
                  )}
                </span>
                {activeTab === "prices"
                  ? "Price Database"
                  : "Project Parameters"}
              </h2>

              {activeTab === "prices" ? (
                <div className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
                  {/* Search */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Search Materials
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={priceSearchQuery}
                        onChange={(e) => setPriceSearchQuery(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handlePriceSearch()
                        }
                        placeholder="Search materials..."
                      />
                      <Button onClick={handlePriceSearch} size="sm">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Category Selector */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
                      Material Categories
                    </label>
                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                      {materialPrices &&
                        Object.entries(materialPrices.materials).map(
                          ([key, cat]) => (
                            <button
                              key={key}
                              onClick={() => {
                                setSelectedPriceCategory(key);
                                setSearchResults([]);
                                setPriceSearchQuery("");
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                selectedPriceCategory === key
                                  ? "bg-teal-500 text-white shadow-sm"
                                  : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                              }`}
                            >
                              <div className="font-medium capitalize">
                                {cat.category}
                              </div>
                              <div className="text-xs opacity-75">
                                {Object.keys(cat.items).length} items
                              </div>
                            </button>
                          ),
                        )}
                    </div>
                  </div>

                  {/* Database Info */}
                  {materialPrices && (
                    <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="text-xs text-gray-500 mb-1">
                        Database Info
                      </div>
                      <div className="text-sm text-teal-700 font-medium">
                        {materialPrices.metadata.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        v{materialPrices.metadata.version} • Updated{" "}
                        {materialPrices.metadata.last_updated}
                      </div>
                    </div>
                  )}
                </div>
              ) : activeTab === "estimate" ? (
                <div className="flex flex-col h-full">
                  {/* Form fields - compact two-column layout */}
                  <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                    <div className="col-span-2">
                      <AddressAutocomplete
                        label="Project Address"
                        placeholder={`123 Main Street SW, ${isSupported ? city?.name || "Calgary" : "Calgary"}`}
                        value={estimateForm.project_address}
                        onChange={(value) =>
                          setEstimateForm({
                            ...estimateForm,
                            project_address: value,
                          })
                        }
                        onSelect={handleAddressSelect}
                      />
                      {/* Show detected zone/community info */}
                      {(selectedZone || selectedCommunity) && (
                        <div className="mt-1.5 flex flex-wrap gap-2">
                          {selectedZone && (
                            <Badge variant="info" className="text-xs">
                              Zone: {selectedZone}
                            </Badge>
                          )}
                          {selectedCommunity && (
                            <Badge variant="info" className="text-xs">
                              {selectedCommunity}
                            </Badge>
                          )}
                          <span className="text-xs text-gray-400 italic">
                            Defaults adjusted for location
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Project Type
                      </label>
                      <select
                        value={estimateForm.project_type}
                        onChange={(e) =>
                          handleProjectTypeChange(e.target.value)
                        }
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 shadow-sm text-sm"
                      >
                        {projectTypes.length > 0 ? (
                          projectTypes.map((type) => (
                            <option key={type.code} value={type.code}>
                              {type.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="residential_single">
                              Single Family
                            </option>
                            <option value="residential_multi">
                              Multi-Family
                            </option>
                            <option value="commercial_retail">Retail</option>
                            <option value="commercial_office">Office</option>
                            <option value="industrial_light">Industrial</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Quality Level
                      </label>
                      <select
                        value={estimateForm.quality_level}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            quality_level: e.target.value,
                          })
                        }
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 shadow-sm text-sm"
                      >
                        {qualityLevels.length > 0 ? (
                          qualityLevels.map((level) => (
                            <option key={level.code} value={level.code}>
                              {level.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="economy">Economy</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                            <option value="luxury">Luxury</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <Input
                        label="Floor Area (SF)"
                        type="number"
                        value={estimateForm.gross_floor_area_sf || ""}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            gross_floor_area_sf: Number(e.target.value),
                          })
                        }
                        placeholder="1,800-2,500"
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        Typical Calgary: 1,800-2,500 SF
                      </span>
                    </div>

                    <div>
                      <Input
                        label="Floors"
                        type="number"
                        value={estimateForm.num_floors || ""}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            num_floors: Number(e.target.value),
                          })
                        }
                        placeholder="1-3"
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        Above grade floors
                      </span>
                    </div>

                    <div>
                      <Input
                        label="Basement (SF)"
                        type="number"
                        value={estimateForm.basement_area_sf || ""}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            basement_area_sf: Number(e.target.value),
                          })
                        }
                        placeholder="800-1,200"
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        Optional - Calgary avg: 900 SF
                      </span>
                    </div>

                    <div>
                      <Input
                        label="Garage (SF)"
                        type="number"
                        value={estimateForm.garage_area_sf || ""}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            garage_area_sf: Number(e.target.value),
                          })
                        }
                        placeholder="400-600"
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        2-car: 440 SF, 3-car: 660 SF
                      </span>
                    </div>

                    <div className="col-span-2">
                      <Input
                        label="Contingency (%)"
                        type="number"
                        value={estimateForm.contingency_percent || ""}
                        onChange={(e) =>
                          setEstimateForm({
                            ...estimateForm,
                            contingency_percent: Number(e.target.value),
                          })
                        }
                        placeholder="10-15"
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        Recommended: 10% renovations, 15% new builds
                      </span>
                    </div>
                  </div>

                  {/* Calculate Button - always visible at bottom */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <Button
                      onClick={calculateEstimate}
                      disabled={isLoading}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Calculating...
                        </>
                      ) : (
                        "Calculate Estimate"
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                /* BOQ Tab */
                <div className="flex flex-col h-full">
                  {/* Form fields - compact grid layout */}
                  <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                    <Input
                      label="Floor Area (SF)"
                      type="number"
                      value={boqForm.gross_floor_area_sf || ""}
                      onChange={(e) =>
                        setBoqForm({
                          ...boqForm,
                          gross_floor_area_sf: Number(e.target.value),
                        })
                      }
                    />

                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Quality
                      </label>
                      <select
                        value={boqForm.quality_level}
                        onChange={(e) =>
                          setBoqForm({
                            ...boqForm,
                            quality_level: e.target.value,
                          })
                        }
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 shadow-sm text-sm"
                      >
                        {qualityLevels.length > 0 ? (
                          qualityLevels.map((level) => (
                            <option key={level.code} value={level.code}>
                              {level.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="economy">Economy</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                            <option value="luxury">Luxury</option>
                          </>
                        )}
                      </select>
                    </div>

                    <Input
                      label="Floors"
                      type="number"
                      value={boqForm.num_floors || ""}
                      onChange={(e) =>
                        setBoqForm({
                          ...boqForm,
                          num_floors: Number(e.target.value),
                        })
                      }
                    />

                    <Input
                      label="Bedrooms"
                      type="number"
                      value={boqForm.num_bedrooms || ""}
                      onChange={(e) =>
                        setBoqForm({
                          ...boqForm,
                          num_bedrooms: Number(e.target.value),
                        })
                      }
                    />

                    <Input
                      label="Bathrooms"
                      type="number"
                      value={boqForm.num_bathrooms || ""}
                      onChange={(e) =>
                        setBoqForm({
                          ...boqForm,
                          num_bathrooms: Number(e.target.value),
                        })
                      }
                    />

                    {/* Basement toggle + area inline */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setBoqForm({
                            ...boqForm,
                            has_basement: !boqForm.has_basement,
                          })
                        }
                        className={`w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                          boqForm.has_basement ? "bg-teal-500" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
                            boqForm.has_basement
                              ? "translate-x-5"
                              : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <div className="flex-1">
                        <Input
                          label="Basement (SF)"
                          type="number"
                          value={boqForm.basement_area_sf || ""}
                          onChange={(e) =>
                            setBoqForm({
                              ...boqForm,
                              basement_area_sf: Number(e.target.value),
                              has_basement: true,
                            })
                          }
                          disabled={!boqForm.has_basement}
                          placeholder={boqForm.has_basement ? "" : "Off"}
                        />
                      </div>
                    </div>

                    {/* Garage toggle + area inline */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setBoqForm({
                            ...boqForm,
                            has_garage: !boqForm.has_garage,
                          })
                        }
                        className={`w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                          boqForm.has_garage ? "bg-teal-500" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
                            boqForm.has_garage
                              ? "translate-x-5"
                              : "translate-x-0.5"
                          }`}
                        />
                      </button>
                      <div className="flex-1">
                        <Input
                          label="Garage (SF)"
                          type="number"
                          value={boqForm.garage_area_sf || ""}
                          onChange={(e) =>
                            setBoqForm({
                              ...boqForm,
                              garage_area_sf: Number(e.target.value),
                              has_garage: true,
                            })
                          }
                          disabled={!boqForm.has_garage}
                          placeholder={boqForm.has_garage ? "" : "Off"}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Generate Button - always visible at bottom */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <Button
                      onClick={generateBOQ}
                      disabled={isLoading}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        "Generate BOQ"
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Permit Fees Reference */}
            {activeTab !== "prices" && (
              <Card variant="outlined">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4 text-teal-500" />
                  {isSupported ? city?.name || "Calgary" : "Calgary"} Permit
                  Fees
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Rates applied to construction value
                </p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-gray-600">
                    <span>Building Permit</span>
                    <span className="font-mono text-teal-600 font-medium">
                      1.014%
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Trade Permits</span>
                    <span className="font-mono text-teal-600 font-medium">
                      0.979%
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600 pt-2 border-t border-gray-100">
                    <span>Minimum Fee</span>
                    <span className="font-mono text-teal-600 font-medium">
                      $114.00
                    </span>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === "estimate" && estimateResult && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard
                    title="Construction Cost"
                    value={formatCurrency(estimateResult.adjusted_construction)}
                    subtitle="Hard costs"
                  />
                  <SummaryCard
                    title="Soft Costs"
                    value={formatCurrency(estimateResult.total_soft_costs)}
                    subtitle="Fees, contingency"
                  />
                  <SummaryCard
                    title="Permit Fees"
                    value={formatCurrency(estimateResult.permit_fees)}
                    subtitle="City of Calgary"
                  />
                  <SummaryCard
                    title="Total Project"
                    value={formatCurrency(estimateResult.total_project_cost)}
                    subtitle={`${formatCurrency(estimateResult.final_cost_per_sf)}/SF`}
                    highlight
                  />
                </div>

                {/* Cost Breakdown */}
                <Card variant="elevated">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Cost Breakdown by CSI Division
                  </h3>
                  <DivisionBarChart
                    breakdown={estimateResult.division_breakdown}
                  />
                </Card>

                {/* Detailed Costs */}
                <Card variant="elevated">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Cost Analysis
                  </h3>
                  <div className="space-y-4">
                    <CostGauge
                      value={estimateResult.adjusted_construction}
                      max={estimateResult.total_project_cost}
                      label="Construction"
                      color="#14b8a6"
                    />
                    <CostGauge
                      value={estimateResult.contingency_amount}
                      max={estimateResult.total_project_cost}
                      label={`Contingency (${estimateResult.contingency_percent}%)`}
                      color="#f59e0b"
                    />
                    <CostGauge
                      value={estimateResult.design_fees_amount}
                      max={estimateResult.total_project_cost}
                      label={`Design Fees (${estimateResult.design_fees_percent}%)`}
                      color="#10b981"
                    />
                    <CostGauge
                      value={estimateResult.permit_fees}
                      max={estimateResult.total_project_cost}
                      label="Permit Fees"
                      color="#3b82f6"
                    />
                    <CostGauge
                      value={estimateResult.other_soft_costs}
                      max={estimateResult.total_project_cost}
                      label="Other Soft Costs"
                      color="#8b5cf6"
                    />
                  </div>
                </Card>

                {/* Adjustment Factors */}
                <div className="grid grid-cols-2 gap-4">
                  <Card variant="outlined">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Location Factor
                    </div>
                    <div className="text-2xl font-mono text-teal-600 mt-1">
                      {estimateResult.location_factor.toFixed(2)}x
                    </div>
                    <div className="text-xs text-gray-500">
                      {isSupported ? city?.name || "Calgary" : "Calgary"} base
                    </div>
                  </Card>
                  <Card variant="outlined">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Inflation Factor
                    </div>
                    <div className="text-2xl font-mono text-teal-600 mt-1">
                      {estimateResult.inflation_factor.toFixed(3)}x
                    </div>
                    <div className="text-xs text-gray-500">2024 adjustment</div>
                  </Card>
                </div>
              </>
            )}

            {activeTab === "boq" && boqResult && (
              <div className="space-y-4">
                {/* Rate Settings Panel */}
                <Card
                  variant="outlined"
                  className="border-amber-200 bg-amber-50/50"
                >
                  <button
                    onClick={() => setShowRateSettings(!showRateSettings)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-amber-600" />
                      <span className="font-medium text-amber-800">
                        Rate Settings & Adjustments
                      </span>
                      <Badge variant="warning" size="sm">
                        Editable
                      </Badge>
                    </div>
                    {showRateSettings ? (
                      <ChevronUp className="h-4 w-4 text-amber-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-amber-600" />
                    )}
                  </button>

                  {showRateSettings && (
                    <div className="mt-4 pt-4 border-t border-amber-200 space-y-4">
                      <div className="text-xs text-amber-700 mb-3">
                        Adjust rates to match your actual costs. Labour rates
                        based on{" "}
                        {isSupported
                          ? `${city?.name || "Calgary"} ${new Date().getFullYear()}`
                          : `Calgary ${new Date().getFullYear()}`}{" "}
                        market (CIQS Alberta + local surveys).
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Labour Rate Adj.
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0.5"
                              max="1.5"
                              step="0.05"
                              value={rateSettings.labourRateMultiplier}
                              onChange={(e) =>
                                setRateSettings({
                                  ...rateSettings,
                                  labourRateMultiplier: parseFloat(
                                    e.target.value,
                                  ),
                                })
                              }
                              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                            />
                            <span className="text-sm font-mono text-indigo-600 w-12">
                              {(
                                (rateSettings.labourRateMultiplier - 1) *
                                100
                              ).toFixed(0)}
                              %
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Material Rate Adj.
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0.5"
                              max="1.5"
                              step="0.05"
                              value={rateSettings.materialRateMultiplier}
                              onChange={(e) =>
                                setRateSettings({
                                  ...rateSettings,
                                  materialRateMultiplier: parseFloat(
                                    e.target.value,
                                  ),
                                })
                              }
                              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                            />
                            <span className="text-sm font-mono text-amber-600 w-12">
                              {(
                                (rateSettings.materialRateMultiplier - 1) *
                                100
                              ).toFixed(0)}
                              %
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Overhead %
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={rateSettings.overheadPercent}
                            onChange={(e) =>
                              setRateSettings({
                                ...rateSettings,
                                overheadPercent:
                                  parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Profit %
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={rateSettings.profitPercent}
                            onChange={(e) =>
                              setRateSettings({
                                ...rateSettings,
                                profitPercent: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <label className="flex items-center gap-2 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={rateSettings.showBreakdown}
                            onChange={(e) =>
                              setRateSettings({
                                ...rateSettings,
                                showBreakdown: e.target.checked,
                              })
                            }
                            className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                          />
                          Show Material/Labour breakdown
                        </label>
                        <button
                          onClick={() =>
                            setRateSettings({
                              labourRateMultiplier: 1.0,
                              materialRateMultiplier: 1.0,
                              overheadPercent: 12,
                              profitPercent: 10,
                              showBreakdown: true,
                            })
                          }
                          className="text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          Reset to defaults
                        </button>
                      </div>
                    </div>
                  )}
                </Card>

                {/* BOQ Table */}
                <Card variant="elevated">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {boqResult.project_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Bill of Quantities • Includes Material + Labour
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportBOQ}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-1" />
                      )}
                      Export BOQ
                    </Button>
                  </div>
                  <BOQTable
                    boq={adjustedBoqResult || boqResult}
                    showBreakdown={rateSettings.showBreakdown}
                    overheadPercent={rateSettings.overheadPercent}
                    profitPercent={rateSettings.profitPercent}
                  />
                </Card>
              </div>
            )}

            {/* Price Database Panel */}
            {activeTab === "prices" && (
              <div className="space-y-6">
                {pricesLoading ? (
                  <Card variant="elevated" className="text-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-teal-500 mx-auto" />
                    <p className="text-gray-500 mt-4">
                      Loading material prices...
                    </p>
                  </Card>
                ) : materialPrices ? (
                  <>
                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <Card
                        variant="outlined"
                        className="bg-teal-50 border-teal-200"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-teal-700">
                            Search Results ({searchResults.length} items)
                          </h3>
                          <button
                            onClick={() => {
                              setSearchResults([]);
                              setPriceSearchQuery("");
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Clear Results
                          </button>
                        </div>
                        <div className="space-y-2">
                          {searchResults.map((result, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                            >
                              <div>
                                <div className="text-gray-800">
                                  {result.description}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {result.category} • {result.unit} • Source:{" "}
                                  {result.source}
                                </div>
                              </div>
                              <div className="text-teal-600 font-mono text-lg font-semibold">
                                ${result.price.toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Live Price Sync Status */}
                    <Card variant="outlined" className="border-emerald-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-emerald-500" />
                          Live Price Sources
                        </h3>
                        <span className="text-xs text-slate-500 px-2 py-1 bg-slate-100 rounded">
                          Managed by admin
                        </span>
                      </div>

                      {/* Currency & Exchange Rate Info */}
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">
                              All prices displayed in CAD
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-blue-600">
                              USD → CAD:
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0.5"
                              max="3"
                              value={usdToCadRate}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val > 0) {
                                  setUsdToCadRate(val);
                                  setExchangeRateDate(
                                    new Date().toISOString().split("T")[0],
                                  );
                                }
                              }}
                              className="w-16 px-2 py-0.5 text-xs font-semibold text-blue-800 bg-white border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="text-[10px] text-blue-400">
                              ({exchangeRateDate})
                            </span>
                            {usdToCadRate !== DEFAULT_USD_TO_CAD_RATE && (
                              <button
                                onClick={() => {
                                  setUsdToCadRate(DEFAULT_USD_TO_CAD_RATE);
                                  setExchangeRateDate(
                                    DEFAULT_EXCHANGE_RATE_DATE,
                                  );
                                }}
                                className="text-[10px] text-blue-500 hover:text-blue-700 underline"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          US-sourced prices (FRED, API Ninjas) are automatically
                          converted •
                          <a
                            href="https://www.bankofcanada.ca/rates/exchange/daily-exchange-rates/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 underline hover:text-blue-800"
                          >
                            Bank of Canada rates
                          </a>
                        </p>
                      </div>

                      {syncStatus && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          {Object.entries(syncStatus).map(([key, status]) => (
                            <div
                              key={key}
                              className={`p-3 rounded-lg border ${
                                status.cache_valid
                                  ? "bg-emerald-50 border-emerald-200"
                                  : "bg-yellow-50 border-yellow-200"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    status.cache_valid
                                      ? "bg-emerald-500"
                                      : "bg-yellow-500"
                                  }`}
                                />
                                <span className="font-medium text-gray-800 text-sm">
                                  {status.source_name}
                                </span>
                                {status.currency && (
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                      status.currency === "CAD"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-green-100 text-green-700"
                                    }`}
                                  >
                                    {status.currency}
                                    {status.currency === "USD" && " → CAD"}
                                  </span>
                                )}
                              </div>
                              {status.description && (
                                <p className="text-xs text-gray-600 mt-1">
                                  {status.description}
                                </p>
                              )}
                              {status.provides &&
                                status.provides.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {status.provides.map((item) => (
                                      <span
                                        key={item}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/70 text-gray-700 border border-gray-200"
                                      >
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200/50">
                                <div className="text-[10px] text-gray-500">
                                  {status.last_sync ? (
                                    <>
                                      Synced:{" "}
                                      {new Date(
                                        status.last_sync,
                                      ).toLocaleDateString()}{" "}
                                      {new Date(
                                        status.last_sync,
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </>
                                  ) : (
                                    "Never synced"
                                  )}
                                </div>
                                <div className="text-[10px] text-gray-400">
                                  TTL: {status.cache_ttl_hours}h
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {scheduledJobs.length > 0 && (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-2">
                            Scheduled Updates
                          </h4>
                          <div className="space-y-1">
                            {scheduledJobs.map((job) => (
                              <div
                                key={job.id}
                                className="flex items-center justify-between text-xs"
                              >
                                <span className="text-gray-600">
                                  {job.name}
                                </span>
                                <span className="text-gray-500">
                                  Next:{" "}
                                  {job.next_run
                                    ? new Date(
                                        job.next_run,
                                      ).toLocaleDateString()
                                    : "Not scheduled"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>

                    {/* Price Sources Reference */}
                    <Card variant="outlined">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <ExternalLink className="w-5 h-5 text-teal-500" />
                        Price Data Sources
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(materialPrices.price_sources).map(
                          ([key, source]) => (
                            <div
                              key={key}
                              className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="font-medium text-gray-800">
                                    {source.name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {source.reliability} reliability •{" "}
                                    {source.update_frequency}
                                  </div>
                                </div>
                                {source.url && (
                                  <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-teal-600 hover:text-teal-700 transition-colors"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </Card>

                    {/* Material Prices Table */}
                    <Card variant="outlined">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800 capitalize">
                          {materialPrices.materials[selectedPriceCategory]
                            ?.category || selectedPriceCategory}{" "}
                          Materials
                        </h3>
                        <Badge variant="info">
                          2025/2026{" "}
                          {isSupported ? city?.name || "Calgary" : "Calgary"}{" "}
                          Prices (CAD)
                        </Badge>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="text-left p-3">Description</th>
                              <th className="text-center p-3 w-20">Unit</th>
                              <th className="text-right p-3 w-28">Price</th>
                              <th className="text-right p-3 w-36">Range</th>
                              <th className="text-center p-3 w-32">Source</th>
                              <th className="text-center p-3 w-28">Updated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materialPrices.materials[selectedPriceCategory] &&
                              Object.entries(
                                materialPrices.materials[selectedPriceCategory]
                                  .items,
                              ).map(([itemId, item]) => (
                                <tr
                                  key={itemId}
                                  className="border-t border-gray-100 hover:bg-gray-50"
                                >
                                  <td className="p-3 text-gray-800">
                                    {item.description}
                                    {item.notes && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {item.notes}
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-3 text-center text-gray-500 uppercase text-xs">
                                    {item.unit}
                                  </td>
                                  <td className="p-3 text-right text-teal-600 font-mono font-semibold">
                                    ${item.price.toFixed(2)}
                                  </td>
                                  <td className="p-3 text-right text-gray-500 font-mono text-xs">
                                    ${item.price_range.low.toFixed(2)} - $
                                    {item.price_range.high.toFixed(2)}
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className="text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-600 border border-gray-200">
                                      {item.source}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center text-gray-500 text-xs">
                                    {item.last_updated}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    {/* Labor Rates */}
                    <Card variant="outlined">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-500" />
                        2025 {isSupported
                          ? city?.name || "Calgary"
                          : "Calgary"}{" "}
                        Labor Rates
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(materialPrices.labor_rates).map(
                          ([trade, data]) => (
                            <div
                              key={trade}
                              className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-gray-700 capitalize">
                                  {trade.replace(/_/g, " ")}
                                </span>
                                <span className="text-blue-600 font-mono font-semibold">
                                  ${data.rate}/hr
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {data.notes}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </Card>

                    {/* Market Trends */}
                    <Card variant="outlined">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                        2025 Market Trends
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(materialPrices.market_trends).map(
                          ([material, data]) => (
                            <div
                              key={material}
                              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-gray-800 font-medium capitalize">
                                  {material}
                                </span>
                                <span
                                  className={`text-sm font-mono ${
                                    data.change_ytd_percent > 0
                                      ? "text-red-500"
                                      : data.change_ytd_percent < 0
                                        ? "text-emerald-500"
                                        : "text-gray-500"
                                  }`}
                                >
                                  {data.change_ytd_percent > 0 ? "+" : ""}
                                  {data.change_ytd_percent}% YTD
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">
                                {data.trend}
                              </div>
                              <div className="text-xs text-gray-500 mt-2">
                                <span className="text-teal-600 font-medium">
                                  2026 Forecast:
                                </span>{" "}
                                {data.forecast_2026}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Source: {data.source}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </Card>
                  </>
                ) : (
                  <Card variant="elevated" className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto" />
                    <h3 className="text-xl text-gray-700 mt-4">
                      Price Database
                    </h3>
                    <p className="text-gray-500 mt-2">
                      Loading material pricing data...
                    </p>
                  </Card>
                )}
              </div>
            )}

            {/* Empty states */}
            {activeTab === "estimate" && !estimateResult && (
              <Card variant="outlined" className="text-center py-12">
                <Calculator className="h-12 w-12 text-gray-300 mx-auto" />
                <h3 className="text-xl text-gray-700 mt-4">
                  Parametric Estimate
                </h3>
                <p className="text-gray-500 mt-2">
                  Enter project parameters and click "Calculate Estimate" to
                  generate a cost breakdown.
                </p>
              </Card>
            )}

            {activeTab === "boq" && !boqResult && (
              <Card variant="outlined" className="text-center py-12">
                <Building2 className="h-12 w-12 text-gray-300 mx-auto" />
                <h3 className="text-xl text-gray-700 mt-4">
                  Bill of Quantities
                </h3>
                <p className="text-gray-500 mt-2">
                  Enter project details and click "Generate BOQ" to create a
                  detailed bill of quantities.
                </p>
              </Card>
            )}
          </div>
        </div>

        {/* FAQ Section for SEO and User Education */}
        <CalgaryConstructionFAQSection />

        {/* Calgary Market Context - shows after results */}
        {(estimateResult || boqResult) && <CalgaryMarketContextCard />}
      </div>
    </>
  );
}
